import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Select,
  MenuItem,
  IconButton,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  Dialog,
} from "@mui/material";
import { colors } from "../../theme/theme.js";
import { Add, CloudUpload, ExpandMore, ExpandLess, Close, KeyboardArrowDown, Email, ZoomIn, ZoomOut, RotateRight, Download } from "@mui/icons-material";
import DateInput from "../common/DateInput";
import FileUploadPopup from "../common/FileUploadPopup";
import { useToast } from "../common/ToastProvider";
import { useSelector } from "react-redux";
import { uploadMemberFiles, fetchMemberByMemberNumber, fetchMemberById, fetchIntroducerByNumber, fetchOptionValues, fetchPincodeDetails, saveMemberDetails, addIntroducer, removeIntroducer, deleteAddress } from "../../services/masters/memberService";
import api from "../../services/api";
import { keyframes } from "@mui/system";

import { focus, calculateAge } from "../../utils/util";
const ImageUploadField = ({ label, field, maxFiles = 1, files, onFileUpload, onFileRemove }) => {
  const [imageBlobUrls, setImageBlobUrls] = useState({});
  const [fileBlobUrls, setFileBlobUrls] = useState({});
  const previousFilePathsRef = useRef('');
  const previousFilesLengthRef = useRef(0);
  const safeFiles = useMemo(() => Array.isArray(files) ? files : (files ? [files] : []), [files]);
  
  // Preview State
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Fetch authenticated images when file paths change
  useEffect(() => {
    const currentFilePaths = safeFiles.map(file => file.path || file.filePath).filter(Boolean).join(',');

    if (currentFilePaths === previousFilePathsRef.current) {
      return;
    }

    if (!currentFilePaths && previousFilePathsRef.current) {
      setImageBlobUrls({});
      setFileBlobUrls({});
      previousFilePathsRef.current = currentFilePaths;
      return;
    }

    previousFilePathsRef.current = currentFilePaths;

    const fetchImages = async () => {
      const newBlobUrls = {};
      for (const file of safeFiles) {
        const filePath = file.path || file.filePath;
        if (filePath && file.type && file.type.startsWith("image/")) {
          try {
            const response = await api.get(filePath, {
              responseType: "blob",
              headers: {
                'Authorization': sessionStorage.getItem('token') || '',
              },
              withCredentials: true,
            });
            const blobUrl = URL.createObjectURL(response.data);
            newBlobUrls[filePath] = blobUrl;
          } catch (error) {
            // Swallow image fetch errors silently in production
          }
        }
      }
      setImageBlobUrls(newBlobUrls);
    };

    if (currentFilePaths) {
      fetchImages();
    }

    return () => {
      Object.values(imageBlobUrls).forEach((blobUrl) => {
        URL.revokeObjectURL(blobUrl);
      });
    };
  }, [safeFiles.length, safeFiles.map(f => f.path || f.filePath).join(',')]);

  useEffect(() => {
    const newFileBlobUrls = {};
    safeFiles.forEach((file, index) => {
      const fileToUse = file instanceof File ? file : file.originalFile;
      if (fileToUse instanceof File && !fileBlobUrls[index]) {
        newFileBlobUrls[index] = URL.createObjectURL(fileToUse);
      }
    });

    if (Object.keys(newFileBlobUrls).length > 0) {
      setFileBlobUrls(prev => ({ ...prev, ...newFileBlobUrls }));
    }
  }, [safeFiles.length, safeFiles.map(f => {
    const fileToUse = f instanceof File ? f : f.originalFile;
    return fileToUse instanceof File ? fileToUse.name + fileToUse.size : f.path || f.name;
  }).join(',')]);

  useEffect(() => {
    const currentLength = safeFiles.length;
    if (currentLength < previousFilesLengthRef.current) {
      setImageBlobUrls({});
      setFileBlobUrls({});
    }
    previousFilesLengthRef.current = currentLength;
  }, [safeFiles.length, field]);

  useEffect(() => {
    return () => {
      Object.values(imageBlobUrls).forEach((blobUrl) => {
        URL.revokeObjectURL(blobUrl);
      });
      Object.values(fileBlobUrls).forEach((blobUrl) => {
        URL.revokeObjectURL(blobUrl);
      });
    };
  }, []);

  const handleImageUpload = async (event) => {
    const fileInput = event.target;
    if (fileInput.files && fileInput.files.length > 0) {
      if (fileInput.disabled) {
        return;
      }
      fileInput.disabled = true;
      try {
        if (safeFiles.length >= maxFiles) {
          await onFileUpload([fileInput.files[0]], field, maxFiles);
        } else {
          await onFileUpload(fileInput.files, field, maxFiles);
        }
      } finally {
        fileInput.disabled = false;
        fileInput.value = "";
      }
    }
  };
  
  // Preview Handlers
  const handlePreviewOpen = (file, index) => {
    setPreviewFile({ ...file, index });
    setZoom(1);
    setRotation(0);
    setPan({ x: 0, y: 0 });
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setPreviewFile(null);
  };
  
  const handleZoomIn = (e) => {
     if (e) e.stopPropagation();
     setZoom(prev => Math.min(prev + 0.5, 5));
  };
  
  const handleZoomOut = (e) => {
     if (e) e.stopPropagation();
     setZoom(prev => Math.max(prev - 0.5, 0.5));
  };
  
  const handleRotate = (e) => {
     if (e) e.stopPropagation();
     setRotation(prev => prev + 90);
  };
  
  const handleDownload = () => {
    if (!previewFile) return;
    const file = previewFile;
    let url = null;
    if ((file.path || file.filePath) && imageBlobUrls[file.path || file.filePath]) {
        url = imageBlobUrls[file.path || file.filePath];
    } else if (fileBlobUrls[file.index]) {
        url = fileBlobUrls[file.index];
    } else if (file instanceof File || file.originalFile instanceof File) {
        const f = file instanceof File ? file : file.originalFile;
        url = URL.createObjectURL(f);
    } else if (file.path || file.filePath) {
        url = file.path || file.filePath;
    }
    
    if (url) {
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name || (file.file && file.file.name) || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
  };

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      e.preventDefault();
      setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  return (
    <>
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          border: "1px dashed #ccc",
          justifyContent: "center",
          height: "100px",
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
          borderRadius: "8px",
          gap: 2,
          position: "relative",
        }}
      >
        {safeFiles.length > 0 ? (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              width: "100%",
              height: "100%",
            }}
          >
            {safeFiles.map((file, index) => (
              <Box
                key={index}
                onClick={() => handlePreviewOpen(file, index)}
                sx={{
                  position: "relative",
                  width: "100%",
                  gap: 2,
                  height: "100%",
                  cursor: "pointer"
                }}
              >
                {(
                  (file.path || file.filePath) && imageBlobUrls[file.path || file.filePath]
                ) ? (
                  <img
                    src={imageBlobUrls[file.path || file.filePath]}
                    alt={file.name || "Uploaded file"}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "4px",
                    }}
                  />
                ) : fileBlobUrls[index] ? (
                  <img
                    src={fileBlobUrls[index]}
                    alt={file.name || "File preview"}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "4px",
                    }}
                  />
                ) : (file instanceof File || file.originalFile instanceof File) ? (
                  <img
                    src={URL.createObjectURL(file instanceof File ? file : file.originalFile)}
                    alt={file.name || "File preview"}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "4px",
                    }}
                  />
                ) : (
                  (file.path || file.filePath) ? (
                    <img
                      src={file.path || file.filePath}
                      alt={file.name || "File"}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "4px",
                      }}
                    />
                  ) :
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        height: "100%",
                        p: 1,
                        gap: 1,
                      }}
                    >
                      <Typography variant="body2" noWrap>
                        {file.fileName || file.name}
                      </Typography>
                    </Box>
                )}
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    onFileRemove(index, field);
                  }}
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    width: 24,
                    height: 24,
                    p: 0.25,
                    backgroundColor: "rgba(0,0,0,0.35)",
                    color: "#fff",
                    '&:hover': { backgroundColor: "rgba(0,0,0,0.5)" },
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        ) : (
          <>
            <Typography variant="body2">{label}</Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUpload />}
              sx={{
                borderColor: colors.primary[300],
                color: colors.primary[700],
                "&:hover": {
                  backgroundColor: colors.primary[50],
                  borderColor: colors.primary[500],
                },
                textTransform: "none",
                mt: 1,
              }}
            >
              Upload
              <input
                type="file"
                hidden
                onChange={handleImageUpload}
                multiple={maxFiles > 1}
                accept="image/*"
              />
            </Button>
          </>
        )}
      </Box>
    </Box>
    
      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handlePreviewClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'common.black',
            boxShadow: 'none',
            backgroundImage: 'none',
            overflow: 'hidden'
          }
        }}
        // Stop accumulation event propagation
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{ 
          position: 'relative', 
          height: '80vh', 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center',
          p: 2
        }}>
           {/* Close Button */}
           <IconButton
            onClick={handlePreviewClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              zIndex: 10,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' }
            }}
          >
            <Close />
          </IconButton>
          
          {/* Controls */}
          {previewFile && (
            <Box sx={{
                position: 'absolute',
                top: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 2,
                zIndex: 10,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: '24px',
                padding: '8px 16px',
            }}>
                 <IconButton onClick={handleZoomIn} sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }} title="Zoom In">
                     <ZoomIn />
                 </IconButton>
                 <IconButton onClick={handleZoomOut} sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }} title="Zoom Out">
                     <ZoomOut />
                 </IconButton>
                 <IconButton onClick={handleRotate} sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }} title="Rotate">
                     <RotateRight />
                 </IconButton>
                 <IconButton onClick={handleDownload} sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }} title="Download">
                     <Download />
                 </IconButton>
            </Box>
          )}

          {/* Image/Content */}
          {previewFile && (
            <Box 
                sx={{ 
                  width: '100%', 
                  height: '100%', 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
            >
                <img
                    src={(() => {
                        const file = previewFile;
                        if ((file.path || file.filePath) && imageBlobUrls[file.path || file.filePath]) {
                            return imageBlobUrls[file.path || file.filePath];
                        } else if (fileBlobUrls[file.index]) {
                            return fileBlobUrls[file.index];
                        } else if (file instanceof File || file.originalFile instanceof File) {
                            return URL.createObjectURL(file instanceof File ? file : file.originalFile);
                        }
                        return file.path || file.filePath || "";
                    })()}
                  alt="Preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                    transition: isDragging ? 'none' : 'transform 0.2s ease-out'
                  }}
                  draggable={false}
                />
            </Box>
          )}

          {/* Footer/Caption */}
          {previewFile && (
            <Box sx={{ 
              position: 'absolute', 
              bottom: 0, 
              left: 0, 
              right: 0, 
              p: 2, 
              background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
              color: 'white',
              textAlign: 'center'
            }}>
              <Typography variant="body1">
                {previewFile.name || previewFile.fileName || "Image Preview"}
              </Typography>
            </Box>
          )}
        </Box>
      </Dialog>
    </>
  );
};

const MemberForm = ({ onFormDataChange, loadedMember = null, resetToken = null }) => {
  const { showError, showSuccess } = useToast();
  const theme = useTheme();
  const { transactionId } = useSelector((state) => state.member || {});

  // Debug: Log any error objects that might be passed as props

  // Define keyframes for spinner animation
  const spin = keyframes`
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  `;

  // Function to create text field styles with matching border colors
  const createTextFieldStyle = (borderColor) => ({
    "& .MuiInputBase-root": {
      height: "40px",
      fontSize: "0.875rem",
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.875rem",
    },
    "& .MuiInputLabel-shrink": {
      transform: "translate(14px, -9px) scale(0.75)",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: borderColor,
      },
      "&:hover fieldset": {
        borderColor: borderColor,
      },
      "&.Mui-focused fieldset": {
        borderColor: borderColor,
      },
    },
  });

  // Text field styles for each card section
  const textFieldStylePrimary = createTextFieldStyle(colors.primary[500]);
  const textFieldStyleSuccess = createTextFieldStyle(colors.success[500]);
  const textFieldStyleWarning = createTextFieldStyle(colors.warning[500]);
  const textFieldStyleError = createTextFieldStyle(colors.error[500]);
  const textFieldStyleSecondary = createTextFieldStyle(colors.secondary[500]);
  const textFieldStyleAccent = createTextFieldStyle(colors.accent[500]);

  // Validation state (removed unused helper text/errors)

  // Form state management
  const [isFormDisabled, setIsFormDisabled] = useState(true);
  const [isLoadingMember, setIsLoadingMember] = useState(false);
  const [isMemberSaved, setIsMemberSaved] = useState(false);
  const [memberNumberEntered, setMemberNumberEntered] = useState(false);
  const [previousMemberCode, setPreviousMemberCode] = useState("");

  // Introducer loading state
  const [isLoadingIntroducer, setIsLoadingIntroducer] = useState(false);
  const [previousIntroducerCode, setPreviousIntroducerCode] = useState("");
  const [currentIntroducerId, setCurrentIntroducerId] = useState("");

  // Introducer list state
  const [introducerList, setIntroducerList] = useState([]);
  const [optionsMap, setOptionsMap] = useState({});
  const [membershipStatusOptions, setMembershipStatusOptions] = useState([]);

  // Pincode lookup state
  const [pincodeData, setPincodeData] = useState({});
  const [isLoadingPincode, setIsLoadingPincode] = useState(false);
  const [pincodeTimeout, setPincodeTimeout] = useState(null);

  // Debug: removed errors logging

  const initialFormData = {
    // Personal Details
    memberId: "",
    memberCode: "",
    applicationId: "",
    ledgerNo: "",
    applicationDate: "",
    regDate: "",
    name: "",
    nameTitle: "",
    gender: "",
    fatherName: "",
    fatherTitle: "",
    soWoCo: "",
    dob: "",
    age: "",
    email: "",
    mobile: "",
    religion: "",
    alternateContactNo: "",
    maritalStatus: "",
    marriageDate: "",

    //addresses - array of address objects
    addresses: [{
      id: "", // Empty ID for new addresses
      address: "",
      addressType: "",
      pincode: "",
      state: "",
      district: "",
      subDistrict: "",
      village: "",
      stateCode: "",
      districtCode: "",
      subDistrictCode: "",
      villageCode: "",
      houseStatus: "",
      careOfName: "",
      contactNo: "",
      defaultAddress: false,
    }],

    // KYC and Bank Details
    aadharNumber: "",
    aadharFiles: [],
    panNumber: "",
    panFiles: [],
    electricityBill: "",
    passportFiles: [],
    voterRationCardNo: "",
    vidFiles: [],
    passportDlNo: "",
    electricityBill: "",
    caFiles: [],
    // passportDlNo: "",
    // voterRationCardNo: "",
    kycVerificationDate: "",
    bankAccountNo: "",
    bankName: "",
    bankBranch: "",
    ifscCode: "",
    bankManager: "",
    bankAddress: "",
    agentCode: "",
    agentName: "",
    leadDate: "",
    introducerId: "",
    introducerNo: "",
    introducerName: "",
    introducerMobile: "",
    introducerRelation: "",
    photographFile: [],
    signatureFile: [],

    // Office Details
    employeeId: "",
    companyName: "",
    occupation: "",
    department: "",
    designation: "",
    headOfficeAddress: "",
    officePostingAddress: "",
    telephoneNo: "",
    reportingManager: "",
    income: "",
    pfGpfNo: "",
    officeJoiningDate: "",

    // Nominee Details
    nomineeName: "",
    nomineeTitle: "",
    nomineeRelation: "",
    nomineeRelationText: "",
    nomineeOccupation: "",
    nomineeMobileNo: "",
    nomineeDob: "",
    nomineeAddress: "",
    nomineeAddressSame: false,
    nomineeAadhaarNo: "",
    nomineePanNo: "",
    nomineeAadharFiles: [],
    nomineePanFiles: [],

    // Status
    active: true,
    membershipStatus: "",
    meetingDate: "",
    resignationDate: "",
    allOperationsBlocked: false,
    eStatement: false,
    eReceipt: false,
    eReceiptAndPrint: false,
    reKyc: false,
  }



  // Comprehensive form data state
  const [formData, setFormData] = useState({ ...initialFormData });
  
  // Autocomplete state for agent code
  const [agentSuggestions, setAgentSuggestions] = useState([]);
  const [showAgentSuggestions, setShowAgentSuggestions] = useState(false);

  // Autocomplete state for bank name
  const [bankSuggestions, setBankSuggestions] = useState([]);
  const [showBankSuggestions, setShowBankSuggestions] = useState(false);


  // Function to filter agent suggestions based on input
  const filterAgentSuggestions = (inputValue) => {
    // Get Society Agent options from optionsMap
    const societyAgentOptions = optionsMap['Society Agent'] || [];
    
    if (!inputValue || inputValue.trim() === '') {
      // Show all options when input is empty
      setAgentSuggestions(societyAgentOptions);
      setShowAgentSuggestions(societyAgentOptions.length > 0);
      return;
    }

    // Filter options based on text containing the input value (case insensitive)
    const filtered = societyAgentOptions.filter(option => 
      option.text && option.text.toLowerCase().includes(inputValue.toLowerCase())
    );
    
    setAgentSuggestions(filtered);
    setShowAgentSuggestions(filtered.length > 0);
  };

  // Handle agent code input change with autocomplete
  const handleAgentCodeChange = (e) => {
    const value = e.target.value;
    handleInputChange("agentCode", value);
    filterAgentSuggestions(value);
  };

  // Handle agent suggestion selection
  const handleAgentSuggestionSelect = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      agentCode: selectedOption.text,  // Use text instead of value
      agentName: selectedOption.text
    }));
    setShowAgentSuggestions(false);
    setAgentSuggestions([]);
  };

  // Function to show all agent options
  const showAllAgentOptions = () => {
    const societyAgentOptions = optionsMap['Society Agent'] || [];
    setAgentSuggestions(societyAgentOptions);
    setShowAgentSuggestions(societyAgentOptions.length > 0);
  };

  // Function to filter bank suggestions based on input
  const filterBankSuggestions = (inputValue) => {
    // Get Banks options from optionsMap
    const bankOptions = optionsMap['Banks'] || [];
    
    if (!inputValue || inputValue.trim() === '') {
      // Show all options when input is empty
      setBankSuggestions(bankOptions);
      setShowBankSuggestions(bankOptions.length > 0);
      return;
    }

    // Filter options based on text containing the input value (case insensitive)
    const filtered = bankOptions.filter(option => 
      option.text && option.text.toLowerCase().includes(inputValue.toLowerCase())
    );
    
    setBankSuggestions(filtered);
    setShowBankSuggestions(filtered.length > 0);
  };

  // Handle bank name input change with autocomplete
  const handleBankNameChange = (e) => {
    const value = e.target.value;
    handleInputChange("bankName", value);
    filterBankSuggestions(value);
  };

  // Handle bank suggestion selection
  const handleBankSuggestionSelect = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      bankName: selectedOption.text
    }));
    setShowBankSuggestions(false);
    setBankSuggestions([]);
  };

  // Function to show all bank options
  const showAllBankOptions = () => {
    const bankOptions = optionsMap['Banks'] || [];
    setBankSuggestions(bankOptions);
    setShowBankSuggestions(bankOptions.length > 0);
  };
  // Prefill when loadedMember is provided by parent (Edit flow)
  useEffect(() => {
    if (!loadedMember) return;
    const m = loadedMember;
    setFormData(prev => ({
      ...prev,
      memberId: m.id || "",
      memberCode: m.memberNo || m.memberCode || "",
      applicationId: m.applicationNo || "",
      ledgerNo: m.ledgerNo || "",
      applicationDate: m.applicationDate || "",
      regDate: m.membershipDate || "",
      name: m.fullName || "",
      nameTitle: m.nameTitle || "",
      gender: m.gender || "",
      fatherName: m.fatherName || m.parentName || m.motherName || "",
      fatherTitle: m.fatherTitle || "",
      soWoCo: m.soWoCo || "",
      dob: m.dob || m.dateOfBirth || "",
      age: m.age || "",
      email: m.email || m.emailId || "",
      mobile: m.mobileNo || m.mobile || "",
      religion: m.religion || "",
      alternateContactNo: m.alternateContactNo || "",
      maritalStatus: m.maritalStatus || "",
      marriageDate: m.marriageDate || m.anniversaryDate || "",
      // KYC and Bank Details
      aadharNumber: getFallbackNumberFromMember(m, ['aadhaarNo', 'aadharNo', 'aadhaar', 'aadhar']) || "",
      panNumber: getFallbackNumberFromMember(m, ['panNo', 'pan']) || "",
      electricityBill: getFallbackNumberFromMember(m, ['electricityBill', 'bill']) || "",
      voterRationCardNo: getFallbackNumberFromMember(m, ['voterRationCardNo', 'voterRationCard', 'voterCard', 'rationCard', 'voterRationCardNo']) || "",
      passportDlNo: getFallbackNumberFromMember(m, ['passportDlNo', 'passportDl', 'passportNo', 'dlNo', 'dlNumber', 'passportDlNo']) || "",
      kycVerificationDate: m.kycVerificationDate || m.kycCompletedDate || "",
      bankAccountNo: m.bankAccountNo || m.accountNumber || "",
      bankName: m.bankName || m.bank || "",
      bankBranch: m.bankBranch || m.bankBranchName || "",
      ifscCode: m.ifscCode || m.ifsc || "",
      bankManager: m.bankManager || m.bankbankManager || "",
      bankAddress: m.bankAddress || "",
      agentCode: m.agentDetails || m.agentCode || m.agent || "",
      agentName: m.agentName || "",
      leadDate: m.leadDate || "",
      introducerId: "",
      ntroducerNo: "",
      introducerName: "",
      introducerMobile: "",
      introducerRelation: "",
      // Office Details
      employeeId: m.employeeId || "",
      companyName: m.companyName || "",
      department: m.department || "",
      occupation: m.occupation || m.profession || m.job || "",
      designation: m.designation || "",
      headOfficeAddress: m.headOfficeAddress || m.officeAddress || "",
      officePostingAddress: m.officePostingAddress || "",
      telephoneNo: m.telephoneNo || "",
      reportingManager: m.reportingManager || "",
      income: m.income || m.monthlyincome || "",
      pfGpfNo: m.pfGpfNo || "",
      officeJoiningDate: m.officeJoiningDate || "",
      // Nominee Details
      nomineeName: m.nomineeName || m.nominee?.name || "",
      nomineeTitle: m.nomineeTitle || m.nominee?.title || "Mr",
      nomineeRelation: m.nomineeRelation || m.nominee?.relation || "",

      nomineeRelationText: (() => {
        const value = m.nomineeRelation || m.nominee?.relation || "";
        if (!value) return "";
        let relationOptions = optionsMap.RELATION || optionsMap['Relation'] || optionsMap.NOMINEE_RELATION || optionsMap['Nominee Relation'] || optionsMap.MEMBER_RELATION;
        if (!relationOptions) {
          const possibleGroups = ['RELATION', 'Relation', 'NOMINEE_RELATION', 'Nominee Relation', 'MEMBER_RELATION'];
          for (const groupName of possibleGroups) {
            if (optionsMap[groupName]) {
              relationOptions = optionsMap[groupName];
              break;
            }
          }
        }
        const matched = relationOptions?.find(opt => opt.value === value || opt.optionValue === value);
        return matched ? (matched.text || matched.optionText || "") : "";
      })(),
      nomineeOccupation: m.nomineeOccupation || m.nominee?.occupation || "",
      nomineeMobileNo: m.nomineeMobileNo || m.nominee?.mobile || "",
      nomineeDob: m.nomineeDob || m.nominee?.dob || "",
      nomineeAddress: m.nomineeAddress || m.nominee?.address || "",
      nomineeAddressSame: m.nomineeAddressSame || false,
      nomineeAadhaarNo: m.nomineeAadhaarNo || m.nominee?.aadharNumber || "",
      nomineePanNo: m.nomineePanNo || m.nominee?.panNumber || "",
      // Files (existing)
      nomineeAadharFiles: m.documents?.filter(doc => doc.documentType === 'NOMINEE_AADHAAR')?.map(doc => ({ id: doc.id, fileName: doc.fileName, filePath: doc.filePath, fileType: doc.fileType, isExisting: true })) || [],
      nomineePanFiles: m.documents?.filter(doc => doc.documentType === 'NOMINEE_PAN')?.map(doc => ({ id: doc.id, fileName: doc.fileName, filePath: doc.filePath, fileType: doc.fileType, isExisting: true })) || [],
      // Status
      active: m.active !== undefined ? m.active : true,
      membershipStatus: m.membershipStatus || m.status || "",
      meetingDate: m.meetingDate || "",
      resignationDate: m.resignationDate || "",
      allOperationsBlocked: m.allOperationsBlocked || false,
      eStatement: m.eStatement || false,
      eReceipt: m.eReceipt || false,
      eReceiptAndPrint: m.eReceiptAndPrint || false,
      reKyc: m.reKyc || false,
      // Files (existing)
      aadharFiles: m.documents?.filter(doc => doc.documentType === 'AADHAAR_NO')?.map(doc => ({ id: doc.id, fileName: doc.fileName, filePath: doc.filePath, fileType: doc.fileType, isExisting: true })) || [],
      panFiles: m.documents?.filter(doc => doc.documentType === 'PAN')?.map(doc => ({ id: doc.id, fileName: doc.fileName, filePath: doc.filePath, fileType: doc.fileType, isExisting: true })) || [],
      passportFiles: m.documents?.filter(doc => doc.documentType === 'PASSPORT_NO')?.map(doc => ({ id: doc.id, fileName: doc.fileName, filePath: doc.filePath, fileType: doc.fileType, isExisting: true })) || [],
      vidFiles: m.documents?.filter(doc => doc.documentType === 'VOTER_ID')?.map(doc => ({ id: doc.id, fileName: doc.fileName, filePath: doc.filePath, fileType: doc.fileType, isExisting: true })) || [],
      caFiles: m.documents?.filter(doc => doc.documentType === 'ELECTRICITY_BILL')?.map(doc => ({ id: doc.id, fileName: doc.fileName, filePath: doc.filePath, fileType: doc.fileType, isExisting: true })) || [],
      // Photograph & Signature (include path/type so previews render)
      photographFile: (() => {
        const photoDoc = m.documents?.find(doc => doc.documentType === 'PHOTOGRAPH');
        if (photoDoc) {
          const downloadUrl = photoDoc.filePath
            ? `${process.env.REACT_APP_API_URL}/file/download?file=${photoDoc.filePath}`
            : undefined;
          return [{
            id: photoDoc.id,
            fileName: photoDoc.fileName,
            name: photoDoc.fileName,
            filePath: photoDoc.filePath,
            path: downloadUrl,
            type: (photoDoc.fileType && photoDoc.fileType.startsWith('image/')) ? photoDoc.fileType : 'image/jpeg',
            isExisting: true,
          }];
        }
        return formData.photographFile || [];
      })(),
      signatureFile: (() => {
        const sigDoc = m.documents?.find(doc => doc.documentType === 'SIGNATURE');
        if (sigDoc) {
          const downloadUrl = sigDoc.filePath
            ? `${process.env.REACT_APP_API_URL}/file/download?file=${sigDoc.filePath}`
            : undefined;
          return [{
            id: sigDoc.id,
            fileName: sigDoc.fileName,
            name: sigDoc.fileName,
            filePath: sigDoc.filePath,
            path: downloadUrl,
            type: (sigDoc.fileType && sigDoc.fileType.startsWith('image/')) ? sigDoc.fileType : 'image/jpeg',
            isExisting: true,
          }];
        }
        return formData.signatureFile || [];
      })(),
      // Addresses
      addresses: Array.isArray(m.addresses) && m.addresses.length > 0
        ? m.addresses.map(addr => ({
          id: addr.id || "",
          fkMemberId: addr.fkMemberId || "",
          address: addr.address || "",
          addressType: addr.addressType || addr.addressTypeText || "",
          pincode: addr.pinCode || addr.pincode || "",
          state: addr.stateName || addr.state || "",
          district: addr.districtName || addr.district || "",
          subDistrict: addr.subDistrictName || addr.subDistrict || addr.taluka || addr.tehsil || "",
          village: addr.villageName || addr.village || addr.city || "",
          stateCode: addr.stateCode || "",
          districtCode: addr.districtCode || "",
          subDistrictCode: addr.subDistrictCode || "",
          villageCode: addr.villageCode || "",
          contactNo: addr.contactNo || m.mobileNo || "",
          houseStatus: addr.houseStatus || addr.ownership || addr.houseOwnership || "",
          careOfName: addr.careOf || addr.careOfName || "",
          defaultAddress: addr.defaultAddress || false,
        }))
        : (prev.addresses || [])
    }));
    // Prefill introducer table from loaded member
    if (Array.isArray(m.introducers) && m.introducers.length > 0) {
      setIntroducerList(m.introducers.map(intro => ({
        introducerNo: intro.introducerNo || "",
        introducerName: intro.introducerName || "",
        introducerMobile: intro.introducerMobile || "",
        introducerRelation: intro.introducerRelation || "",
        introducerId: intro.id || intro.introducerId || "",
      })));
    } else {
      setIntroducerList([]);
    }
    // Enable form fields for editing when data is prefilled
    setIsFormDisabled(false);
    setIsMemberSaved(true);
    
    // Force re-render to update UI with populated documents
    setFormData(prev => ({
      ...prev,
      _lastUpdate: Date.now()
    }));
  }, [loadedMember]);


  // Reset form when resetToken changes (Add New flow)
  useEffect(() => {
    if (resetToken === null) return;
    setFormData({ ...initialFormData });
    setIsFormDisabled(true);
    setIsMemberSaved(false);
    setHasSubmittedFirstSeven(false);
    setIntroducerList([]);
  }, [resetToken]);


  // Safety check to ensure addresses array is properly initialized
  useEffect(() => {
    // Only initialize addresses if we're not loading member data and addresses are empty
    if (!isLoadingMember && (!formData.addresses || !Array.isArray(formData.addresses) || formData.addresses.length === 0)) {
      setFormData(prev => ({
        ...prev,
        addresses: [{
          address: "",
          addressType: "",
          pincode: "",
          state: "",
          district: "",
          subDistrict: "",
          village: "",
          houseStatus: "",
          careOfName: "",
          contactNo: "",
        }]
      }));
    }
  }, [isLoadingMember]);

  // File upload popup states
  const [fileUploadPopup, setFileUploadPopup] = useState({
    open: false,
    field: null,
    title: "",
  });

  // File upload loading state
  const [uploadingFiles, setUploadingFiles] = useState({});

  // Address expansion state - track which address is expanded (only one at a time)
  const [expandedAddress, setExpandedAddress] = useState(0); // First address expanded by default

  // Helper function to safely extract error messages
  // Removed getErrorMessage helper as helperText is not used

  // Handle member number search (called manually via Tab key)
  const handleMemberNumberChange = async (memberNumber) => {
    try {
      if (!memberNumber || memberNumber.trim() === '') {
        setIsFormDisabled(true);
        setMemberNumberEntered(false);
        setPreviousMemberCode("");
        return;
      }

      // Check if the member code has actually changed - prevent API call completely
      if (memberNumber === previousMemberCode) {
        return; // Don't make API call if same member code
      }

      setIsLoadingMember(true);
      setMemberNumberEntered(true);

      try {
        // First try to get member by member number
        const response = await fetchMemberByMemberNumber(memberNumber);

        if (response.data.statuscode === 200 && response.data.data) {
          // Member found - this API includes all member data including documents
          const memberData = response.data.data;
          
          
          
          // Check for any occupation-related fields
          const occupationFields = Object.keys(memberData).filter(key =>
            key.toLowerCase().includes('occupation') ||
            key.toLowerCase().includes('profession') ||
            key.toLowerCase().includes('job') ||
            key.toLowerCase().includes('work')
          );


          setFormData(prev => ({
            ...prev,
            // Personal Details
            memberId: memberData.id || "", // Store the member ID
            memberCode: memberNumber, // Preserve the entered member code
            applicationId: memberData.applicationNo || "",
            ledgerNo: memberData.ledgerNo || "",
            applicationDate: memberData.applicationDate || "",
            regDate: memberData.membershipDate || "",
            name: memberData.fullName || "",
            nameTitle: memberData.nameTitle || "",
            gender: memberData.gender || "",
            fatherName: memberData.fatherName || memberData.parentName || memberData.motherName || "",
            fatherTitle: memberData.fatherTitle || "",
            soWoCo: memberData.soWoCo || "",
            dob: memberData.dob || memberData.dateOfBirth || "",
            age: memberData.age || "",
            email: memberData.email || memberData.emailId || "",
            mobile: memberData.mobileNo || memberData.mobile || "",
            religion: memberData.religion || "",
            alternateContactNo: memberData.alternateContactNo || "",
            occupation: memberData.occupation || memberData.profession || memberData.job || "",
            maritalStatus: memberData.maritalStatus || "",
            marriageDate: memberData.marriageDate || memberData.anniversaryDate || "",

            // KYC and Bank Details
            aadharNumber: getFallbackNumberFromMember(memberData, ['aadhaarNo', 'aadharNo', 'aadhaar', 'aadhar']) || "",
            panNumber: getFallbackNumberFromMember(memberData, ['panNo', 'pan']) || "",
            electricityBill: getFallbackNumberFromMember(memberData, ['electricityBill', 'nso']) || "",
            voterRationCardNo: getFallbackNumberFromMember(memberData, ['voterRationCardNo', 'voterRationCard', 'voterCard', 'rationCard', 'voterRationCardNo']) || "",
            passportDlNo: getFallbackNumberFromMember(memberData, ['passportDlNo', 'passportDl', 'passportNo', 'dlNo', 'dlNumber', 'passportDlNo']) || "",
            kycVerificationDate: memberData.kycVerificationDate || memberData.kycCompletedDate || "",
            bankAccountNo: memberData.bankAccountNo || memberData.accountNumber || "",
            bankName: memberData.bankName || memberData.bank || "",
            bankBranch: memberData.bankBranch || memberData.bankBranchName || "",
            ifscCode: memberData.ifscCode || memberData.ifsc || "",
            bankManager: memberData.bankManager || memberData.bankbankManager || "",
            bankAddress: memberData.bankAddress || "",
            agentCode: memberData.agentDetails || memberData.agentCode || memberData.agent || "",
            agentName: memberData.agentName || "",
            leadDate: memberData.leadDate || "",
            introducerId: "",
            introducerNo: "",
            introducerName: "",
            introducerMobile: "",
            introducerRelation: "",

            // Office Details
            employeeId: memberData.employeeId || "",
            companyName: memberData.companyName || "",
            department: memberData.department || "",
            designation: memberData.designation || "",
            headOfficeAddress: memberData.headOfficeAddress || memberData.officeAddress || "",
            officePostingAddress: memberData.officePostingAddress || "",
            telephoneNo: memberData.telephoneNo || "",
            reportingManager: memberData.reportingManager || "",
            income: memberData.income || memberData.monthlyincome || "",
            pfGpfNo: memberData.pfGpfNo || "",
            officeJoiningDate: memberData.officeJoiningDate || "",

            // Nominee Details
            nomineeName: memberData.nomineeName || memberData.nominee?.name || "",
            nomineeTitle: memberData.nomineeTitle || memberData.nominee?.title || "Mr",
            nomineeRelation: memberData.nomineeRelation || memberData.nominee?.relation || "",
            nomineeOccupation: memberData.nomineeOccupation || memberData.nominee?.occupation || "",
            nomineeMobileNo: memberData.nomineeMobileNo || memberData.nominee?.mobile || "",
            nomineeDob: memberData.nomineeDob || memberData.nominee?.dob || "",
            nomineeAddress: memberData.nomineeAddress || memberData.nominee?.address || "",
            nomineeAddressSame: memberData.nomineeAddressSame || false,
            nomineeAadhaarNo: memberData.nomineeAadhaarNo || memberData.nominee?.aadharNumber || "",
            nomineePanNo: memberData.nomineePanNo || memberData.nominee?.panNumber || "",
            // Files (existing)
            nomineeAadharFiles: memberData.documents?.filter(doc => doc.documentType === 'NOMINEE_AADHAAR')?.map(doc => ({
              id: doc.id,
              fileName: doc.fileName,
              filePath: doc.filePath,
              fileType: doc.fileType,
              isExisting: true
            })) || [],
            nomineePanFiles: memberData.documents?.filter(doc => doc.documentType === 'NOMINEE_PAN')?.map(doc => ({
              id: doc.id,
              fileName: doc.fileName,
              filePath: doc.filePath,
              fileType: doc.fileType,
              isExisting: true
            })) || [],

            // Status
            active: memberData.active !== undefined ? memberData.active : true,
            membershipStatus: memberData.membershipStatus || memberData.status || "",
            meetingDate: memberData.meetingDate || "",
            resignationDate: memberData.resignationDate || "",
            allOperationsBlocked: memberData.allOperationsBlocked || false,
            eStatement: memberData.eStatement || false,
            eReceipt: memberData.eReceipt || false,
            eReceiptAndPrint: memberData.eReceiptAndPrint || false,
            reKyc: memberData.reKyc || false,
            // Populate file fields with document data
            aadharFiles: (() => {
              const aadharDocs = memberData.documents?.filter(doc => doc.documentType === 'AADHAAR_NO') || [];
              return aadharDocs.map(doc => ({
              id: doc.id,
              fileName: doc.fileName,
              filePath: doc.filePath,
              fileType: doc.fileType,
              isExisting: true
              }));
            })(),
            panFiles: (() => {
              const panDocs = memberData.documents?.filter(doc => doc.documentType === 'PAN') || [];
              return panDocs.map(doc => ({
              id: doc.id,
              fileName: doc.fileName,
              filePath: doc.filePath,
              fileType: doc.fileType,
              isExisting: true
              }));
            })(),
            passportFiles: (() => {
              const nsoDocs = memberData.documents?.filter(doc => doc.documentType === 'PASSPORT_NO') || [];
              return nsoDocs.map(doc => ({
              id: doc.id,
              fileName: doc.fileName,
              filePath: doc.filePath,
              fileType: doc.fileType,
              isExisting: true
              }));
            })(),
            vidFiles: (() => {
              const vidDocs = memberData.documents?.filter(doc => doc.documentType === 'VOTER_ID') || [];
              return vidDocs.map(doc => ({
              id: doc.id,
              fileName: doc.fileName,
              filePath: doc.filePath,
              fileType: doc.fileType,
              isExisting: true
              }));
            })(),
            caFiles: (() => {
              const caDocs = memberData.documents?.filter(doc => doc.documentType === 'ELECTRICITY_BILL') || [];
              return caDocs.map(doc => ({
              id: doc.id,
              fileName: doc.fileName,
              filePath: doc.filePath,
              fileType: doc.fileType,
              isExisting: true
              }));
            })(),
            photographFile: (() => {
              // Look for photograph in documents array
              const photoDoc = memberData.documents?.find(doc =>
                doc.documentType === 'PHOTOGRAPH'
              );
              if (photoDoc) {
                return [{
                  id: photoDoc.id,
                  fileName: photoDoc.fileName,
                  filePath: photoDoc.filePath,
                  fileType: photoDoc.fileType || 'image/jpeg',
                  path: `${process.env.REACT_APP_API_URL}/file/download?file=${photoDoc.filePath}`,
                  name: photoDoc.fileName,
                  type: photoDoc.fileType || 'image/jpeg',
                  isExisting: true
                }];
              }
              return [];
            })(),
            signatureFile: (() => {
              // Look for signature in documents array
              const sigDoc = memberData.documents?.find(doc =>
                doc.documentType === 'SIGNATURE'
              );

              if (sigDoc) {
                return [{
                  id: sigDoc.id,
                  fileName: sigDoc.fileName,
                  filePath: sigDoc.filePath,
                  fileType: sigDoc.fileType || 'image/jpeg',
                  path: `${process.env.REACT_APP_API_URL}/file/download?file=${sigDoc.filePath}`,
                  name: sigDoc.fileName,
                  type: sigDoc.fileType || 'image/jpeg',
                  isExisting: true
                }];
              }
              return [];
            })(),
            introducer: "",
            // Auto-fill addresses from member data
            addresses: memberData.addresses && Array.isArray(memberData.addresses) && memberData.addresses.length > 0
              ? (() => {
                const mappedAddresses = memberData.addresses.map(addr => {
                  return {
                    id: addr.id || "",
                    fkMemberId: addr.fkMemberId || "",
                    address: addr.address || "",
                    addressType: addr.addressType || "",
                    pincode: addr.pinCode || addr.pincode || "",
                    state: addr.stateName || addr.state || "",
                    district: addr.districtName || addr.district || "",
                    subDistrict: addr.subDistrictName || addr.subDistrict || addr.taluka || addr.tehsil || "",
                    village: addr.villageName || addr.village || addr.city || "",
                    stateCode: addr.stateCode || "",
                    districtCode: addr.districtCode || "",
                    subDistrictCode: addr.subDistrictCode || "",
                    villageCode: addr.villageCode || "",
                    houseStatus: addr.houseStatus || addr.houseOwnership || addr.ownership || "owned",
                    careOfName: addr.careOf || addr.careOfName || addr.careOfPerson || "",
                    contactNo: addr.contactNo || addr.contactNumber || addr.phone || addr.mobile || ""
                  };
                });
                return mappedAddresses;
              })()
              : [{
                id: "", // Empty ID for new addresses
                address: "",
                addressType: "",
                pincode: "",
                state: "",
                district: "",
                subDistrict: "",
                village: "",
                stateCode: "",
                districtCode: "",
                subDistrictCode: "",
                villageCode: "",
                houseStatus: "",
                careOfName: "",
                contactNo: ""
              }],
            // Add more field mappings as needed
          }));



          // Auto-fill introducer list from member data
          if (memberData.introducers && Array.isArray(memberData.introducers) && memberData.introducers.length > 0) {
            setIntroducerList(memberData.introducers.map(intro => ({
              introducerNo: intro.introducerNo || "",
              introducerName: intro.introducerName || "",
              introducerMobile: intro.introducerMobile || "",
              introducerRelation: intro.introducerRelation || "",
              introducerId: intro.id || intro.introducerId || ""
            })));
          } else {
            setIntroducerList([]);
          }


          // Debug: Check the actual values being set from memberData


          // Enable form for editing
          setIsFormDisabled(false);
          setIsMemberSaved(true);
          setPreviousMemberCode(memberNumber); // Update the previous member code after successful API call
            
            // Force re-render to update UI with populated documents
            setFormData(prev => ({
              ...prev,
              _lastUpdate: Date.now()
            }));
        } else {
          // Member not found - enable form for new member creation
          setIsFormDisabled(false);
          setIsMemberSaved(false);
          setPreviousMemberCode(memberNumber); // Update the previous member code even when member not found
          // showError("Member not found. Form enabled for new member creation.");
        }
      } catch (error) {

        if (error.response?.status === 404) {
          // Member not found - enable form for new member creation
          setIsFormDisabled(false);
          setIsMemberSaved(false);
          setPreviousMemberCode(memberNumber); // Update the previous member code for 404 error
          setFormData(prev => ({
            ...initialFormData,
            memberCode: memberNumber // Preserve the entered member code
          }));
        } else {
          showError("Error fetching member data. Please try again.");
          setIsFormDisabled(true);
          setIsMemberSaved(false);
        }
      } finally {
        setIsLoadingMember(false);
      }
    } catch (error) {
      setIsLoadingMember(false);
    }
  };

  // Handle introducer input change - detects if it's a number and calls API
  const handleIntroducerChange = async (value) => {

    if (!value || value.trim() === '') {
      setFormData(prev => ({
        ...prev,
        introducerNo: ""
      }));
      setCurrentIntroducerId("");
      setPreviousIntroducerCode("");
      return;
    }

    // Check if the input is a number (member number)
    const isNumber = /^\d+$/.test(value.trim());

    if (!isNumber) {
      // If it's not a number, treat it as a manual name entry
      setFormData(prev => ({
        ...prev,
        introducerNo: ""
      }));
      setCurrentIntroducerId("");
      return;
    }

    // It's a number - check if we should call the API
    // Only call API if mobile field is empty
    if (formData.introducerMobile && formData.introducerMobile.trim() !== '') {
      // Mobile field has a value, don't call API
      setFormData(prev => ({
        ...prev,
        introducerNo: value // Just store the number
      }));
      return;
    }

    // Check if the introducer code has actually changed
    if (value === previousIntroducerCode) {
      return;
    }

    setIsLoadingIntroducer(true);

    try {
      const response = await fetchIntroducerByNumber(value);

      if (response.data.statuscode === 200 && response.data.data) {
        // Introducer found - populate details
        const introducerData = response.data.data;

        setFormData(prev => ({
          ...prev,
          introducer: introducerData.fullName || "",
          introducerNo: value, // Store the member number
          introducerMobile: introducerData.mobileNo || introducerData.mobile || "",
          introducerRelation: prev.introducerRelation || "" // Keep existing relation if any
        }));

        // Store the introducer's ID for API calls
        setCurrentIntroducerId(introducerData.id || "");
        setPreviousIntroducerCode(value);
      } else {
        // Introducer not found - allow manual entry
        setFormData(prev => ({
          ...prev,
          introducerNo: "",
          introducerMobile: prev.introducerMobile || "",
          introducerRelation: prev.introducerRelation || ""
        }));
        setCurrentIntroducerId("");
        setPreviousIntroducerCode(value);
        showSuccess("Member not found. You can enter details manually.");
      }
    } catch (error) {
      console.error('Error fetching introducer data');

      if (error.response?.status === 404) {
        // Introducer not found - allow manual entry
        setFormData(prev => ({
          ...prev,
          introducerNo: "",
          introducerMobile: prev.introducerMobile || "",
          introducerRelation: prev.introducerRelation || ""
        }));
        setCurrentIntroducerId("");
        setPreviousIntroducerCode(value);
        showSuccess("Member not found. You can enter details manually.");
      } else {
        showError("Error fetching introducer data. Please try again.");
      }
    } finally {
      setIsLoadingIntroducer(false);
    }
  };

  // Add introducer to list
  const addIntroducerToList = async () => {
    if (!formData.introducer) {
      showError("Please enter an introducer name or number");
      return;
    }

    if (!formData.memberId) {
      showError("Member ID is required to add introducer");
      return;
    }

    if (!formData.memberId) {
      showError("Please save member details first");
      return;
    }

    // Check if introducer already exists in the list
    // If ID exists, check by ID/No. If manual, check by Name.
    const exists = introducerList.some(intro =>
      (currentIntroducerId && intro.introducerNo === formData.introducerNo) ||
      (!currentIntroducerId && intro.introducerName.toLowerCase() === formData.introducer.toLowerCase())
    );

    if (exists) {
      showError("This introducer is already in the list");
      return;
    }

    try {
      // Prepare the introducer data for API call
      const introducerData = {
        fkMemberId: formData.memberId,
        fkIntroducingMemberId: currentIntroducerId || null,
        introducerName: formData.introducer,
        introducerMobile: formData.introducerMobile,
        introducerRelation: formData.introducerRelation,
        introducerNo: currentIntroducerId ? formData.introducerNo : null,
        introducerType: "REGISTRATION"
      };

      // Make API call to add introducer
      const response = await addIntroducer(introducerData);

      if (response.data.statuscode === 200) {
        // API call successful - add to local list
        setIntroducerList(prev => [...prev, {
          introducerNo: currentIntroducerId ? formData.introducerNo : "", // Only keep Number if it was a linked member
          introducerName: formData.introducer,
          introducerMobile: formData.introducerMobile,
          introducerRelation: formData.introducerRelation, // Saving the value code
          introducerId: currentIntroducerId || null // Null if manual
        }]);

        // Clear the form fields
        setFormData(prev => ({
          ...prev,
          introducerNo: "",
          introducer: "",
          introducerMobile: "",
          introducerRelation: ""
        }));
        setCurrentIntroducerId("");
        setPreviousIntroducerCode("");

        showSuccess("Introducer added successfully");
      } else {
        showError("Failed to add introducer. Please try again.");
      }
    } catch (error) {
      console.error('Error adding introducer');
      showError("Error adding introducer. Please try again.");
    }
  };

  // Remove introducer from list
  const removeIntroducerFromList = async (index) => {
    const introducerToRemove = introducerList[index];

    if (!introducerToRemove.introducerId) {
      showError("Cannot remove introducer: Missing introducer ID");
      return;
    }

    try {
      // Prepare the data for API call to deactivate the introducer
      const introducerData = {
        id: introducerToRemove.introducerId,
        active: false
      };

      // Make API call to remove/deactivate introducer
      const response = await removeIntroducer(introducerData);

      if (response.data.statuscode === 200) {
        // API call successful - remove from local list
        setIntroducerList(prev => prev.filter((_, i) => i !== index));
        showSuccess("Introducer removed successfully");
      } else {
        showError("Failed to remove introducer. Please try again.");
      }
    } catch (error) {
      console.error('Error removing introducer');
      showError("Error removing introducer. Please try again.");
    }
  };

  // Helper function to clean form data - only send non-empty values
  const cleanFormData = (data) => {
    const cleaned = {};
    Object.keys(data).forEach(key => {
      const value = data[key];
      // Only include non-empty values (not empty strings, null, or undefined)
      if (value !== "" && value !== null && value !== undefined) {
        cleaned[key] = value;
      }
    });
    return cleaned;
  };

  // Handle POST request after first 6 fields are filled
  const handleFirstSevenFieldsSubmit = async () => {
    try {
      // Map form data to API format - only include first 6 required fields
      const apiData = cleanFormData({
        applicationNo: formData.applicationId || "",
        ledgerNo: formData.ledgerNo || "",
        applicationDate: formData.applicationDate,
        memberNo: formData.memberCode,
        membershipDate: formData.regDate,
        nameTitle: formData.nameTitle,
        fullName: formData.name,
        membershipStatus: "Active", // Default value
      });

      console.log('API payload for first 6 fields:', apiData);
      
      // Use the existing saveMemberDetails function which handles authentication
      const response = await saveMemberDetails(apiData);

      // Extract and store newly created member id if present
      const createdMemberId =
        response?.data?.data?.id ||
        response?.data?.data?.memberId ||
        response?.data?.data?.member?.id ||
        response?.data?.data?.member?.memberId ||
        null;

      if (createdMemberId) {
        setFormData((prev) => ({
          ...prev,
          memberId: createdMemberId,
        }));
      }

      // Enable form for editing after successful member creation
      setIsFormDisabled(false);
      setIsMemberSaved(true);

    } catch (error) {
      console.error("Error submitting member data");
      showError("Error submitting member data. Please try again.");
    }
  };

  // Manual save state
  const [hasSubmittedFirstSeven, setHasSubmittedFirstSeven] = useState(false);

  // Handle pincode change with debounced API call
  const handlePincodeChange = (pincode, addressIndex) => {
    // Clear existing timeout
    if (pincodeTimeout) {
      clearTimeout(pincodeTimeout);
    }

    if (!pincode || pincode.trim() === '' || pincode.length !== 6) {
      // Clear pincode data for this address
      setPincodeData(prev => ({
        ...prev,
        [addressIndex]: null
      }));
      return;
    }

    const timeout = setTimeout(async () => {
      setIsLoadingPincode(true);
      try {
        const response = await fetchPincodeDetails(pincode);
        if (response.data.statuscode === 200 && response.data.data) {
          const pincodeDetails = response.data.data;

          // Process the data to create unique options for dropdowns
          const districts = [...new Map(pincodeDetails.map(item => [item.districtCode, {
            code: item.districtCode,
            name: item.districtName,
            stateCode: item.stateCode,
            stateName: item.stateName
          }])).values()];

          const subDistricts = [...new Map(pincodeDetails.map(item => [item.subDistrictCode, {
            code: item.subDistrictCode,
            name: item.subDistrictName,
            districtCode: item.districtCode
          }])).values()];

          const villages = [...new Map(pincodeDetails.map(item => [item.villageCode, {
            code: item.villageCode,
            name: item.villageName,
            subDistrictCode: item.subDistrictCode
          }])).values()];

          setPincodeData(prev => ({
            ...prev,
            [addressIndex]: {
              districts,
              subDistricts,
              villages,
              rawData: pincodeDetails
            }
          }));

          // Auto-fill state if only one state
          if (districts.length > 0 && districts[0].stateName) {
            setFormData(prev => ({
              ...prev,
              addresses: prev.addresses.map((addr, idx) =>
                idx === addressIndex
                  ? { ...addr, state: districts[0].stateName, stateCode: districts[0].stateCode }
                  : addr
              )
            }));
          }
        } else {
          setPincodeData(prev => ({
            ...prev,
            [addressIndex]: null
          }));
        }
      } catch (error) {
        console.error('Error fetching pincode details');
        setPincodeData(prev => ({
          ...prev,
          [addressIndex]: null
        }));
      } finally {
        setIsLoadingPincode(false);
      }
    }, 1000); // 1 second delay
    setPincodeTimeout(timeout);
  };

  const handleInputChange = React.useCallback(
    (field, value) => {
      // Special handling for pincode
      if (field.startsWith("addresses.") && field.endsWith(".pincode")) {
        const addressIndex = parseInt(field.split(".")[1]);
        handlePincodeChange(value, addressIndex);
        // Also update the form data immediately so the input shows the value
        setFormData(prev => ({
          ...prev,
          addresses: prev.addresses.map((addr, idx) =>
            idx === addressIndex ? { ...addr, pincode: value } : addr
          )
        }));
        return; // Don't continue with the normal form data update
      }

      // Prevent negative age values
      if (field === "age" && parseInt(value) < 0) {
        return;
      }

      setFormData((prev) => {
        // Handle nested properties (e.g., "address.pincode")
        if (field.includes('.')) {
          const [parentKey, childKey] = field.split('.');
          return {
            ...prev,
            [parentKey]: {
              ...prev[parentKey],
              [childKey]: value,
            },
          };
        }

        // Handle regular properties
        return {
          ...prev,
          [field]: value,
        };
      });

      // Removed inline error clearing as helper text is not used
    },
    []
  );

  const handleMemberAddressChange = (e, index = 0) => {
    const value = e.target.value;
    handleAddressChange(index, "address", value);
    if (formData.nomineeAddressSame && index === 0) {
      handleInputChange("nomineeAddress", value);
    }
  };

  const handleNomineeAddressChange = (e) => {
    if (!formData.nomineeAddressSame) {
      handleInputChange("nomineeAddress", e.target.value);
    }
  };

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    handleInputChange("nomineeAddressSame", checked);
    if (checked) {
      // Use the first address if available
      const firstAddress = formData.addresses?.[0]?.address || "";
      handleInputChange("nomineeAddress", firstAddress);
    }
  };

  // Address management functions
  const handleDefaultAddressChange = (index, checked) => {
    setFormData(prev => ({
      ...prev,
      addresses: prev.addresses.map((addr, i) => ({
        ...addr,
        defaultAddress: i === index ? checked : (checked ? false : addr.defaultAddress)
      }))
    }));
  };

  const addNewAddress = () => {
    const newAddress = {
      id: "", // Empty ID for new addresses
      address: "",
      addressType: "residence",
      pincode: "",
      state: "",
      district: "",
      subDistrict: "",
      village: "",
      stateCode: "",
      districtCode: "",
      subDistrictCode: "",
      villageCode: "",
      houseStatus: "owned",
      careOfName: "",
      contactNo: "",
      defaultAddress: false,
    };

    setFormData(prev => ({
      ...prev,
      addresses: [...prev.addresses, newAddress]
    }));

    // Expand the new address
    const newIndex = formData.addresses.length;

    // Clear pincode data for the new address
    setPincodeData(prev => ({
      ...prev,
      [newIndex]: null
    }));
    setExpandedAddress(newIndex);
  };

  const removeAddress = async (index) => {
    if (formData.addresses.length > 1) {
      const addressToRemove = formData.addresses[index];
      /* debug removed */
      // console.log('address data:', addressToRemove);

      if (addressToRemove.id) {
        try {
          const deletePayload = {
            id: addressToRemove.id,
            fkMemberId: formData.memberId || null,
            // For deletion, id + active=false is sufficient for backend
            active: false
          };
          await deleteAddress(deletePayload);
          showSuccess('Address deleted');
        } catch (error) {
          // If backend returns 404 Not Found but the address is already deleted, treat as success
          if (error?.response?.status === 404) {
            showSuccess('Address removed');
          } else {
            console.error('Error deleting address');
            showError('Failed to delete address from server');
            return; // Don't remove from UI if API call failed with a real error
          }
        }
      }

      // Remove from form data
      setFormData(prev => ({
        ...prev,
        addresses: prev.addresses.filter((_, i) => i !== index)
      }));

      // Update expanded address index
      setExpandedAddress(prev => {
        if (prev === index) {
          // If removing the currently expanded address, expand the first remaining address
          return 0;
        } else if (prev > index) {
          // If expanded address is after the removed one, adjust the index
          return prev - 1;
        }
        return prev; // No change needed
      });

      // Clean up pincode data for removed address
      setPincodeData(prev => {
        const newPincodeData = { ...prev };
        delete newPincodeData[index];
        // Reindex remaining addresses
        const reindexed = {};
        Object.keys(newPincodeData).forEach(key => {
          const keyIndex = parseInt(key);
          if (keyIndex > index) {
            reindexed[keyIndex - 1] = newPincodeData[key];
          } else if (keyIndex < index) {
            reindexed[keyIndex] = newPincodeData[key];
          }
        });
        return reindexed;
      });
    }
  };

  const toggleAddressExpansion = (index) => {
    // If clicking on the currently expanded address, collapse it
    // Otherwise, expand the clicked address (this will automatically collapse others)
    setExpandedAddress(prev => prev === index ? -1 : index);
  };

  const handleAddressChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      addresses: prev.addresses.map((addr, i) =>
        i === index ? { ...addr, [field]: value } : addr
      )
    }));
  };

  const handleFileUpload = async (files, field, maxFiles = 6) => {
    // Prevent concurrent uploads for the same field
    if (uploadingFiles[field]) {
      /* debug removed */
      return;
    }

    const newFiles = Array.from(files);
    const currentFiles = formData[field] || [];

    // For photograph and signature fields, enforce 1 file limit
    if (field === 'photographFile' || field === 'signatureFile') {
      maxFiles = 1;
    }

    if (currentFiles.length + newFiles.length > maxFiles) {
      const fieldName = field === 'photographFile' ? 'photograph' :
        field === 'signatureFile' ? 'signature' : 'files';
      showError(`Maximum ${maxFiles} ${fieldName} allowed`);
      return;
    }

    // Check file sizes - different limits for different field types
    let maxFileSize;
    if (field === 'photographFile' || field === 'signatureFile') {
      maxFileSize = 1 * 1024 * 1024; // 1 MB for photos/signatures
    } else {
      maxFileSize = 1 * 1024 * 1024; // 1MB for other documents
    }

    const oversizedFiles = newFiles.filter((file) => file.size > maxFileSize);
    if (oversizedFiles.length > 0) {
      const sizeLimit = field === 'photographFile' || field === 'signatureFile' ? '1MB' : '1MB';
      showError(
        `Files too large. Maximum size is ${sizeLimit} per file.`
      );
      return;
    }

    // For photograph and signature fields, upload immediately when selected
    if ((field === 'photographFile' || field === 'signatureFile') && transactionId) {
      try {
        setUploadingFiles((prev) => ({ ...prev, [field]: true }));

        // Upload each new file immediately
        const uploadPromises = newFiles.map(async (file) => {
          const uploadFormData = new FormData();

          // Ensure we're appending a proper File object
          if (file instanceof File) {
            uploadFormData.append("file", file);
          } else {
            throw new Error("Invalid file object");
          }

          const documentType = getDocumentType(field);
          const response = await uploadMemberFiles(transactionId, formData.memberId, documentType, uploadFormData);

          // Extract file info from response - handle different possible response structures
          let uploadedFile;
          if (response.data.data && response.data.data.documents && response.data.data.documents.length > 0) {
            uploadedFile = response.data.data.documents[0];
          } else if (response.data.data && response.data.data.files && response.data.data.files.length > 0) {
            uploadedFile = response.data.data.files[0];
          } else if (response.data.data && response.data.data.file) {
            uploadedFile = response.data.data.file;
          } else if (response.data.data) {
            // If the response data itself is the file info
            uploadedFile = response.data.data;
          } else {
            throw new Error('Invalid response structure from upload API');
          }

          // Create a clean file object with both local and server data
          const fileObject = {
            // Server data - map from the actual response structure
            path: uploadedFile.absolutePath || uploadedFile.path,
            filePath: uploadedFile.filePath,
            type: uploadedFile.fileType,
            name: uploadedFile.fileName,
            size: uploadedFile.fileSizeInKb ? uploadedFile.fileSizeInKb * 1024 : uploadedFile.size,
            ext: uploadedFile.fileExtension,
            id: uploadedFile.id || uploadedFile.fkMemberId, // Try different possible ID fields
            // Keep original file for blob URL if it exists
            originalFile: file instanceof File ? file : null,
          };

          // Log the document ID for debugging
          /* debug removed */

          return fileObject;
        });

        const uploadedFiles = await Promise.all(uploadPromises);

        // Update form data with uploaded file info
        setFormData((prev) => {
          const currentFiles = prev[field] || [];
          // For single-file fields (photograph, signature), replace the file
          return {
            ...prev,
            [field]: uploadedFiles, // Replace with new files
          };
        });

        // Force a re-render by updating a timestamp
        setFormData((prev) => ({
          ...prev,
          _lastUpdate: Date.now(),
        }));

        showSuccess(`${field === 'photographFile' ? 'Photograph' : 'Signature'} uploaded successfully`);
      } catch (error) {
        showError(`Failed to upload ${field === 'photographFile' ? 'photograph' : 'signature'}. Please try again.`);
      } finally {
        setUploadingFiles((prev) => ({ ...prev, [field]: false }));
      }
      return;
    }

    if (transactionId) {
      try {
        setUploadingFiles((prev) => ({ ...prev, [field]: true }));

        // Upload each new file
        const uploadPromises = newFiles.map(async (file) => {
          const uploadFormData = new FormData();

          // Ensure we're appending a proper File object
          if (file instanceof File) {
            uploadFormData.append("file", file);
          } else {
            throw new Error("Invalid file object");
          }

          const documentType = getDocumentType(field);
          const response = await uploadMemberFiles(transactionId, formData.memberId, documentType, uploadFormData);

          // Extract file info from response - handle different possible response structures
          let uploadedFile;
          if (response.data.data && response.data.data.documents && response.data.data.documents.length > 0) {
            uploadedFile = response.data.data.documents[0];
          } else if (response.data.data && response.data.data.files && response.data.data.files.length > 0) {
            uploadedFile = response.data.data.files[0];
          } else if (response.data.data && response.data.data.file) {
            uploadedFile = response.data.data.file;
          } else if (response.data.data) {
            // If the response data itself is the file info
            uploadedFile = response.data.data;
          } else {
            throw new Error('Invalid response structure from upload API');
          }

          // Create a clean file object with both local and server data
          const fileObject = {
            // Server data - map from the actual response structure
            path: uploadedFile.absolutePath || uploadedFile.path,
            filePath: uploadedFile.filePath,
            type: uploadedFile.fileType,
            name: uploadedFile.fileName,
            size: uploadedFile.fileSizeInKb ? uploadedFile.fileSizeInKb * 1024 : uploadedFile.size,
            ext: uploadedFile.fileExtension,
            id: uploadedFile.id || uploadedFile.fkMemberId, // Try different possible ID fields
            // Keep original file for blob URL if it exists
            originalFile: file instanceof File ? file : null,
          };

          // Log the document ID for debugging
          /* debug removed */

          return fileObject;
        });

        const uploadedFiles = await Promise.all(uploadPromises);

        // Update form data with uploaded file info
        setFormData((prev) => {
          const currentFiles = prev[field] || [];
          // For single-file fields (photograph, signature), replace the file
          if (field === 'photographFile' || field === 'signatureFile') {
            return {
              ...prev,
              [field]: uploadedFiles, // Replace with new files
            };
          } else {
            // For multi-file fields, add to existing files
            return {
              ...prev,
              [field]: [...currentFiles, ...uploadedFiles],
            };
          }
        });

        // Force a re-render by updating a timestamp
        setFormData((prev) => ({
          ...prev,
          _lastUpdate: Date.now(),
        }));


        showSuccess(`Uploaded successfully`);
      } catch (error) {

        // Handle specific error types
        if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
          showError("Network error. Please check your connection and try again.");
        } else if (error.response?.status === 413) {
          showError("File too large. Please select a smaller file (under 1MB recommended).");
        } else if (error.response?.status === 400) {
          const errorMessage = error.response?.data?.message || "Invalid file format or document type.";
          showError(`Upload failed: ${errorMessage}`);
        } else if (error.response?.status === 401) {
          showError("Authentication required. Please log in again.");
        } else if (error.response?.status === 403) {
          showError("Permission denied. You don't have access to upload files.");
        } else if (error.response?.status >= 500) {
          showError("Server error. Please try again later.");
        } else {
          showError("Failed to upload files. Please try again.");
        }
      } finally {
        setUploadingFiles((prev) => ({ ...prev, [field]: false }));
      }
    } else {
      // Fallback: store files without uploading if no transactionId
      setFormData((prev) => ({
        ...prev,
        [field]: [...(prev[field] || []), ...newFiles],
      }));

    }
  };

  const handleFileRemove = async (index, field) => {
    const currentFiles = Array.isArray(formData[field]) ? formData[field] : (formData[field] ? [formData[field]] : []);
    const fileToRemove = currentFiles[index];

    // Remove from form data immediately for better UX
    setFormData((prev) => ({
      ...prev,
      [field]: (Array.isArray(prev[field]) ? prev[field] : (prev[field] ? [prev[field]] : [])).filter((_, i) => i !== index),
    }));

    // If it's a file with a document ID (either existing or newly uploaded), call the delete API
    if (fileToRemove && fileToRemove.id && fileToRemove.id !== 'photograph' && fileToRemove.id !== 'signature') {
      try {
        /* debug removed */
        const deletePayload = {
          data: JSON.stringify({
            id: fileToRemove.id,
            active: false
          }),
          target: "society.models.master.member.MemberDocument",
          compressed: false
        };

        const response = await api.post(`${process.env.REACT_APP_API_URL}/file/delete`, deletePayload, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': sessionStorage.getItem('token') || '',
          },
          withCredentials: true,
        });

        if (response.data.statuscode === 200) {
          showSuccess('File deleted successfully');
        } else {
          showError('Failed to delete file from server');
        }
      } catch (error) {
        showError('Error deleting file. Please try again.');
      }
    }
  };

  // Helper function to get document type from field name
  const getDocumentType = (field) => {
    const fieldToDocumentType = {
      'aadharFiles': 'AADHAAR_NO',
      'panFiles': 'PAN',
      'passportFiles': 'PASSPORT_NO',
      'vidFiles': 'VOTER_ID',
      'caFiles': 'ELECTRICITY_BILL',
      'photographFile': 'PHOTOGRAPH',
      'signatureFile': 'SIGNATURE',
      'nomineeAadharFiles': 'NOMINEE_AADHAAR',
      'nomineePanFiles': 'NOMINEE_PAN'
    };
    return fieldToDocumentType[field] || 'Other';
  };

  // Helper: detect NSO docs from various backend shapes/labels (also include legacy 'Other')
  const isNsoDocument = (doc) => {
    if (!doc) return false;
    const typeRaw = (doc.documentType || doc.type || doc.category || '').toString().trim().toUpperCase();
    const nameRaw = (doc.documentName || doc.name || doc.title || '').toString().trim().toUpperCase();
    const isTypeMatch = ['NSO', 'NSO_NO', 'OTHER', 'PASSPORT_NO'].includes(typeRaw);
    const isNameMatch = nameRaw.includes('NSO') || nameRaw === 'OTHER' || nameRaw.includes('PASSPORT');
    return isTypeMatch || isNameMatch;
  };

  const getFallbackNumberFromMember = (memberData, keys = []) => {
    for (const key of keys) {
      if (memberData && memberData[key]) return memberData[key];
    }
    return '';
  };

  const buildDownloadUrl = (filePath) => {
    if (!filePath) return '';
    const isBase64 = /^[A-Za-z0-9+/=]+$/.test(filePath);
    const encoded = isBase64 ? filePath : btoa(filePath);
    return `${process.env.REACT_APP_API_URL}/file/download?file=${encoded}`;
  };

  // Function to prepare existing files for a field (provide path/type for thumbnails)
  const fetchExistingFiles = async (field) => {
    if (!formData.memberId) {
      return [];
    }

    try {
      const currentFiles = formData[field] || [];

      // Filter files that have filePath (existing files from member data)
      const existingFiles = currentFiles.filter(file => file.filePath);

      if (existingFiles.length === 0) {
        return [];
      }

      // Build objects with direct download URL in path so popup can fetch blobs
      const mapToPopupFile = (doc) => {
        const fileName = doc.fileName || doc.filePath.split('/').pop() || 'Unknown file';
        const ext = (fileName.split('.').pop() || '').toLowerCase();
        const guessedType = doc.fileType || (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext) ? `image/${ext === 'jpg' ? 'jpeg' : ext}` : `application/${ext || 'octet-stream'}`);
        // Use filePath as provided by API (it is already base64-encoded server-side)
        const downloadUrl = `${process.env.REACT_APP_API_URL}/file/download?file=${doc.filePath}`;

        return {
          // For FileUploadPopup expectations
          id: doc.id, // Include the document ID for deletion
          path: downloadUrl, // popup will fetch and create blob URL
          name: fileName,
          type: guessedType,
          // Keep originals for later save
          fileName: fileName,
          filePath: doc.filePath,
          fileType: guessedType,
          isExisting: true,
        };
      };

      return existingFiles.map(mapToPopupFile);
    } catch (error) {
      return [];
    }
  };

  // File upload popup handlers
  const handleOpenFileUpload = async (field, title) => {
    setFileUploadPopup({
      open: true,
      field,
      title,
    });

    // Fetch existing files for this field
    const existingFiles = await fetchExistingFiles(field);

    // Update the file upload popup with existing files
    // This will be handled by the FileUploadPopup component
    setFileUploadPopup(prev => ({
      ...prev,
      existingFiles: existingFiles
    }));
  };

  const handleCloseFileUpload = () => {
    setFileUploadPopup({
      open: false,
      field: null,
      title: "",
      existingFiles: [],
    });
  };

  const handleFilesSelected = async (selectedFiles) => {
    const { field } = fileUploadPopup;

    // For photograph and signature fields, upload immediately when selected
    if (field && (field === 'photographFile' || field === 'signatureFile') && transactionId) {
      // Filter out already uploaded files and only process new files
      const newFiles = selectedFiles.filter(
        (fileObj) => fileObj.file && fileObj.file instanceof File
      );
      const existingFiles = selectedFiles.filter(
        (fileObj) => fileObj.path && !fileObj.file
      );

      if (newFiles.length > 0) {
        try {
          setUploadingFiles((prev) => ({ ...prev, [field]: true }));

          // Upload each new file immediately
          const uploadPromises = newFiles.map(async (fileObj) => {
            const uploadFormData = new FormData();

            // Ensure we're appending a proper File object
            if (fileObj.file instanceof File) {
              uploadFormData.append("file", fileObj.file);
            } else {
              console.error("Invalid file object");
              throw new Error("Invalid file object");
            }

            const documentType = getDocumentType(field);
            const response = await uploadMemberFiles(transactionId, formData.memberId, documentType, uploadFormData);

            // Extract file info from response - handle different possible response structures
            let uploadedFile;
            if (response.data.data && response.data.data.documents && response.data.data.documents.length > 0) {
              uploadedFile = response.data.data.documents[0];
            } else if (response.data.data && response.data.data.files && response.data.data.files.length > 0) {
              uploadedFile = response.data.data.files[0];
            } else if (response.data.data && response.data.data.file) {
              uploadedFile = response.data.data.file;
            } else if (response.data.data) {
              // If the response data itself is the file info
              uploadedFile = response.data.data;
            } else {
              throw new Error('Invalid response structure from upload API');
            }

            // Create a clean file object with both local and server data
            return {
              // Server data - map from the actual response structure
              path: uploadedFile.absolutePath || uploadedFile.path,
              filePath: uploadedFile.filePath,
              type: uploadedFile.fileType,
              name: uploadedFile.fileName,
              size: uploadedFile.fileSizeInKb ? uploadedFile.fileSizeInKb * 1024 : uploadedFile.size,
              ext: uploadedFile.fileExtension,
              id: uploadedFile.id || uploadedFile.fkMemberId, // Try different possible ID fields
              // Keep original file for blob URL if it exists
              originalFile: fileObj.file instanceof File ? fileObj.file : null,
            };
          });

          const uploadedFiles = await Promise.all(uploadPromises);

          // Combine existing files with newly uploaded files
          const allFiles = [...existingFiles, ...uploadedFiles];

          // Update form data with all file info
          setFormData((prev) => ({
            ...prev,
            [field]: allFiles,
          }));

          // Force a re-render by updating a timestamp
          setFormData((prev) => ({
            ...prev,
            _lastUpdate: Date.now(),
          }));

          showSuccess(`${field === 'photographFile' ? 'Photograph' : 'Signature'} uploaded successfully`);
        } catch (error) {
          showError(`Failed to upload ${field === 'photographFile' ? 'photograph' : 'signature'}. Please try again.`);
        } finally {
          setUploadingFiles((prev) => ({ ...prev, [field]: false }));
        }
      } else {
        // No new files to upload, just update with existing files
        setFormData((prev) => ({
          ...prev,
          [field]: existingFiles,
        }));
      }

      handleCloseFileUpload();
      return;
    }

    if (field && transactionId) {
      // Prevent concurrent uploads for the same field
      if (uploadingFiles[field]) {
        /* debug removed */
        return;
      }

      try {
        setUploadingFiles((prev) => ({ ...prev, [field]: true }));

        // Filter out already uploaded files and only process new files
        const newFiles = selectedFiles.filter(
          (fileObj) => fileObj.file && fileObj.file instanceof File
        );
        const existingFiles = selectedFiles.filter(
          (fileObj) => fileObj.path && !fileObj.file
        );

        if (newFiles.length === 0) {
          // No new files to upload, just update with existing files
          setFormData((prev) => ({
            ...prev,
            [field]: existingFiles,
          }));
          showSuccess(`Files updated successfully`);
          return;
        }

        // Check file sizes (limit to 10MB per file)
        const maxFileSize = 10 * 1024 * 1024; // 10MB in bytes
        const oversizedFiles = newFiles.filter(
          (fileObj) => fileObj.file.size > maxFileSize
        );
        if (oversizedFiles.length > 0) {
          showError(
            `Files too large: ${oversizedFiles
              .map((f) => f.file.name)
              .join(", ")}. Maximum size is 10MB per file.`
          );
          setUploadingFiles((prev) => ({ ...prev, [field]: false }));
          return;
        }

        // Upload each new file and get the file info
        const uploadPromises = newFiles.map(async (fileObj) => {
          const uploadFormData = new FormData();

          // Ensure we're appending a proper File object
          if (fileObj.file instanceof File) {
            uploadFormData.append("file", fileObj.file);
          } else {
            throw new Error("Invalid file object");
          }

          const documentType = getDocumentType(field);
          const response = await uploadMemberFiles(transactionId, formData.memberId, documentType, uploadFormData);

          // Extract file info from response - handle different possible response structures
          let uploadedFile;
          if (response.data.data && response.data.data.documents && response.data.data.documents.length > 0) {
            uploadedFile = response.data.data.documents[0];
          } else if (response.data.data && response.data.data.files && response.data.data.files.length > 0) {
            uploadedFile = response.data.data.files[0];
          } else if (response.data.data && response.data.data.file) {
            uploadedFile = response.data.data.file;
          } else if (response.data.data) {
            // If the response data itself is the file info
            uploadedFile = response.data.data;
          } else {
            throw new Error('Invalid response structure from upload API');
          }

          // Create a clean file object with both local and server data
          return {
            // Server data - map from the actual response structure
            path: uploadedFile.absolutePath || uploadedFile.path,
            filePath: uploadedFile.filePath,
            type: uploadedFile.fileType,
            name: uploadedFile.fileName,
            size: uploadedFile.fileSizeInKb ? uploadedFile.fileSizeInKb * 1024 : uploadedFile.size,
            ext: uploadedFile.fileExtension,
            id: uploadedFile.id || uploadedFile.fkMemberId, // Try different possible ID fields
            // Keep original file for blob URL if it exists
            originalFile: fileObj.file instanceof File ? fileObj.file : null,
          };
        });

        const uploadedFiles = await Promise.all(uploadPromises);

        // Combine existing files with newly uploaded files
        const allFiles = [...existingFiles, ...uploadedFiles];

        // Update form data with all file info
        setFormData((prev) => ({
          ...prev,
          [field]: allFiles,
        }));

        showSuccess(`Files uploaded successfully`);
      } catch (error) {
        showError("Failed to upload files. Please try again.");
      } finally {
        setUploadingFiles((prev) => ({ ...prev, [field]: false }));
      }
    } else if (field) {
      // Fallback: store files without uploading if no transactionId
      setFormData((prev) => ({
        ...prev,
        [field]: selectedFiles.map((f) => f.file), // Extract the actual file objects
      }));
    }
    handleCloseFileUpload();
  };


  // Notify parent component when formData changes - debounced to improve performance
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (onFormDataChange) {
        // Ensure addresses array is valid before sending to parent
        const getOptionTextFromMap = (groupCandidates, value) => {
          if (!value) return "";
          const groups = Array.isArray(groupCandidates) ? groupCandidates : [groupCandidates];
          for (const groupName of groups) {
            const options = optionsMap[groupName];
            if (options && Array.isArray(options)) {
              const match = options.find(opt => opt.value === value);
              if (match) return match.text || "";
            }
          }
          // Last resort: scan all groups
          for (const [, options] of Object.entries(optionsMap)) {
            if (Array.isArray(options)) {
              const match = options.find(opt => opt.value === value);
              if (match) return match.text || "";
            }
          }
          return "";
        };

        const enrichedAddresses = (formData.addresses && Array.isArray(formData.addresses) && formData.addresses.length > 0
          ? formData.addresses
          : [{
            address: "",
            addressType: "",
            pincode: "",
            state: "",
            district: "",
            subDistrict: "",
            village: "",
            houseStatus: "",
            careOfName: "",
            contactNo: "",
          }]
        ).map(addr => ({
          ...addr,
          addressTypeText: getOptionTextFromMap(['ADDRESS_TYPE', 'Address Type'], addr.addressType)
        }));

        const safeFormData = {
          ...formData,
          // Provide plain-text labels alongside values
          reportingManagerText: getOptionTextFromMap(['Disbursing Officer', 'DISBURSING_OFFICER', 'Reporting Manager', 'REPORTING_MANAGER'], formData.reportingManager),
          nameTitleText: getOptionTextFromMap(['Salutation', 'nameTitle', 'TITLE', 'Title'], formData.nameTitle),
          fatherTitleText: getOptionTextFromMap(['FATHER_SALUTATION', 'Father Salutation', 'SALUTATION', 'Salutation', 'TITLE', 'Title'], formData.fatherTitle),
          genderText: getOptionTextFromMap(['GENDER', 'Gender'], formData.gender),
          religionText: getOptionTextFromMap(['RELIGION', 'Religion'], formData.religion),
          occupationText: getOptionTextFromMap(['OCCUPATION', 'Occupation'], formData.occupation || formData.occupation),
          maritalStatusText: getOptionTextFromMap(['MARITAL_STATUS', 'Marital Status'], formData.maritalStatus),
          soWoCoText: getOptionTextFromMap(['SOWOCO', 'SoWoCo', 'RELATION', 'Relation'], formData.soWoCo),
          addresses: enrichedAddresses,
        };
        onFormDataChange(safeFormData);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [formData, onFormDataChange]);


  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (pincodeTimeout) {
        clearTimeout(pincodeTimeout);
      }
    };
  }, [pincodeTimeout]);

  // Fetch option values when form mounts
  useEffect(() => {
    const loadOptions = async () => {
      try {
        /* debug removed */
        const resp = await fetchOptionValues(['MEMBER_MASTER']);
        /* debug removed */

        if (resp?.data?.statuscode === 200 && Array.isArray(resp.data.data)) {
          /* debug removed */
          // Normalize into a map: { [optionName]: values[] }
          const normalized = {};
          for (const group of resp.data.data) {
            if (group && group.optionName && Array.isArray(group.values)) {
              /* debug removed */
              normalized[group.optionName] = group.values.map(v => ({
                id: v.id,
                value: v.optionValue,
                text: v.optionText,
                selected: !!v.selected,
              }));
            }
          }
          /* debug removed */
          // Look for address-related options in any group
          let addressTypeOptions = normalized.ADDRESS_TYPE || normalized['Address Type'];
          if (!addressTypeOptions) {
            // Check if address types are in a different group
            const possibleGroups = ['ADDRESS', 'ADDRESS_TYPE', 'MEMBER_ADDRESS', 'ADDRESS_KIND', 'Address Type'];
            for (const groupName of possibleGroups) {
              if (normalized[groupName]) {
                addressTypeOptions = normalized[groupName];
                break;
              }
            }
          }

          // If still no address options, check all groups for address-related content
          if (!addressTypeOptions) {
            for (const [groupName, options] of Object.entries(normalized)) {
              if (options && options.some(opt =>
                opt.text && (
                  opt.text.toLowerCase().includes('address') ||
                  opt.text.toLowerCase().includes('residence') ||
                  opt.text.toLowerCase().includes('permanent')
                )
              )) {
                addressTypeOptions = options;
                break;
              }
            }
          }

          setOptionsMap(normalized);
          /* debug removed */

          // Clear any form values that don't match available options
          const fieldsToValidate = ['nameTitle', 'fatherTitle', 'soWoCo', 'gender', 'religion', 'occupation', 'maritalStatus'];
          const updates = {};

          fieldsToValidate.forEach(field => {
            const currentValue = formData[field];
            if (currentValue) {
              let options = null;

              // Find the appropriate options for this field
              switch (field) {
                case 'nameTitle':
                  options = normalized.Salutation || normalized['Salutation'] || normalized.nameTitle || normalized['nameTitle'] || normalized.TITLE || normalized['Title'];
                  /* debug removed */
                  break;
                case 'fatherTitle':
                  options = normalized.FATHER_SALUTATION || normalized['Father Salutation'] || normalized.SALUTATION || normalized['Salutation'] || normalized.TITLE || normalized['Title'];
                  break;
                case 'soWoCo':
                  options = normalized.SOWOCO || normalized['SoWoCo'] || normalized.RELATION || normalized['Relation'];
                  break;
                case 'gender':
                  options = normalized.GENDER || normalized['Gender'];
                  break;
                case 'religion':
                  options = normalized.RELIGION || normalized['Religion'];
                  break;
                case 'occupation':
                  options = normalized.OCCUPATION || normalized['Occupation'];
                  break;
                case 'maritalStatus':
                  options = normalized.MARITAL_STATUS || normalized['Marital Status'];
                  break;
              }

              // If options exist and current value doesn't match any option, clear it
              if (options && !options.some(opt => opt.value === currentValue)) {
                updates[field] = "";
              }
            }
          });

          // Apply updates if any
          if (Object.keys(updates).length > 0) {
            setFormData(prev => ({ ...prev, ...updates }));
          }
        }
      } catch (e) {
      }
    };
    loadOptions();
  }, []);

  // Fetch membership status options
  useEffect(() => {
    const loadMembershipStatusOptions = async () => {
      try {
        const resp = await fetchOptionValues(['MEMBER_MASTER']);
        if (resp?.data?.statuscode === 200 && Array.isArray(resp.data.data)) {
          // Find the "Member Account Status" option group
          const membershipStatusGroup = resp.data.data.find(group => 
            group.optionName === "Member Account Status"
          );
          
          if (membershipStatusGroup && Array.isArray(membershipStatusGroup.values)) {
            const options = membershipStatusGroup.values.map(v => ({
              id: v.id,
              value: v.optionValue,
              text: v.optionText,
              selected: !!v.selected,
            }));
            setMembershipStatusOptions(options);
          }
        }
      } catch (error) {
        console.error('Failed to load membership status options:', error);
      }
    };
    loadMembershipStatusOptions();
  }, []);

  // Safety check to ensure addresses array exists before rendering
  // Only apply this check if we're not loading member data
  if (!isLoadingMember && (!formData.addresses || !Array.isArray(formData.addresses) || formData.addresses.length === 0)) {
    setFormData(prev => ({
      ...prev,
      addresses: [{
        address: "",
        addressType: "residence",
        pincode: "",
        state: "",
        district: "",
        subDistrict: "",
        village: "",
        houseStatus: "owned",
        careOfName: "",
        contactNo: "",
      }]
    }));
    return null; // Return null to prevent rendering with corrupted data
  }

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid
          container
          spacing={2}
          sx={{
            flexWrap: {
              xs: "wrap", // mobile: allow wrapping
              md: "nowrap", // tablet: no wrap
              lg: "nowrap", // desktop: no wrap
              xl: "nowrap", // large desktop: no wrap
            },
          }}
        >
          <Grid container xs={12} md={6} spacing={2} sx={{ alignSelf: 'flex-start' }}>
            {/* Personal Details */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: 'fit-content', width: '100%', backgroundColor: (theme) => theme.palette.mode === 'dark' ? `${theme.palette.primary.main}08` : `${theme.palette.primary.main}0A`, borderTop: `3px solid ${theme.palette.primary.main}66` }}>
                <CardContent>
                  <Grid container size={12}>
                    <Grid item xs={12} size={8}>
                      <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }} gutterBottom>
                        Personal Details
                      </Typography>
                    </Grid>
                    <Grid item xs={12} size={4} mb={1}>
                      <TextField
                        fullWidth
                        helperText={
                          loadedMember 
                            ? "Member number cannot be changed in edit mode" 
                            : formData.memberCode 
                              ? "Press Tab to search" 
                              : ""
                        }
                        label="Member Number"
                        size="small"
                        value={formData.memberCode}
                        onChange={(e) => {
                          handleInputChange("memberCode", e.target.value);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Tab' && formData.memberCode) {
                            e.preventDefault();
                            handleMemberNumberChange(formData.memberCode);
                          }
                        }}
                        disabled={isLoadingMember || !!loadedMember}
                        InputProps={{
                          endAdornment: isLoadingMember ? (
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                border: "2px solid #ccc",
                                borderTop: "2px solid transparent",
                                borderRadius: "50%",
                                animation: `${spin} 1s linear infinite`,
                              }}
                            />
                          ) : null,
                        }}
                        sx={textFieldStylePrimary}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={1}>
                    <Grid container size={12}>
                      <Grid item size={3}>
                          <TextField
                            fullWidth
                            label="Application Id"
                            size="small"
                            value={formData.applicationId}
                            onChange={(e) =>
                              handleInputChange(
                                "applicationId",
                                e.target.value
                              )
                            }

                            disabled={isFormDisabled}
                            sx={textFieldStylePrimary}
                          />
                      </Grid>
                      <Grid size={3}>
                          <TextField
                            fullWidth
                            label="Ledger No."
                            size="small"
                          value={formData.ledgerNo}
                            onChange={(e) =>
                              handleInputChange(
                                "ledgerNo",
                                e.target.value
                              )
                            }

                            disabled={isFormDisabled}
                            sx={textFieldStylePrimary}
                          />
                    </Grid>
                      <Grid item size={3}>
                        <DateInput
                          fullWidth
                          label="Resolution Date"
                          size="small"
                          value={formData.applicationDate}
                          onChange={(value) =>
                            handleInputChange("applicationDate", value)
                          }
                          disabled={isFormDisabled}
                          sx={textFieldStylePrimary}
                        />
                      </Grid>
                      <Grid item size={3}>
                        <DateInput
                          fullWidth
                          label="Memership Date"
                          size="small"
                          value={formData.regDate}
                          onChange={(value) =>
                            handleInputChange("regDate", value)
                          }
                          disabled={isFormDisabled}
                          sx={textFieldStylePrimary}
                        />
                      </Grid>
                    </Grid>

                    <Grid container size={12}>
                      <Grid item size={2}>
                        <Select
                          fullWidth
                          displayEmpty
                          size="small"
                          value={formData.nameTitle || ""}
                          onChange={(e) =>
                            handleInputChange("nameTitle", e.target.value)
                          }
                          disabled={isFormDisabled}
                          sx={textFieldStylePrimary}
                          renderValue={(value) => value || "Title"}
                        >
                          <MenuItem value="" disabled>
                            Select Title
                          </MenuItem>
                          {(() => {
                            // Look for salutation options in various possible groups
                            let nameTitleOptions = optionsMap.Salutation || optionsMap['Salutation'] || optionsMap.nameTitle || optionsMap['nameTitle'] || optionsMap.TITLE || optionsMap['Title'];
                            if (!nameTitleOptions) {
                              const possibleGroups = ['Salutation', 'nameTitle', 'TITLE', 'MEMBER_nameTitle', 'MEMBER_TITLE'];
                              for (const groupName of possibleGroups) {
                                if (optionsMap[groupName]) {
                                  nameTitleOptions = optionsMap[groupName];
                                  break;
                                }
                              }
                            }

                            return nameTitleOptions ? (
                              nameTitleOptions.map((option) => (
                                <MenuItem key={option.id || option.value} value={option.text}>
                                  {option.text}
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem disabled>No options available</MenuItem>
                            );
                          })()}
                        </Select>
                      </Grid>
                      <Grid item size={7}>
                        <TextField
                          //  size={9}
                          fullWidth
                          label="Name"
                          size="small"
                          helperText={
                            (() => {
                              const hasAllFields =
                                formData.memberCode &&
                                formData.applicationDate &&
                                formData.regDate &&
                                formData.nameTitle &&
                                formData.name &&
                                formData.gender &&
                                formData.fatherTitle &&
                                formData.fatherName &&
                                formData.soWoCo;

                              return hasAllFields && !isMemberSaved && !hasSubmittedFirstSeven
                                ? "Press Tab to save"
                                : "";
                            })()
                          }
                          value={formData.name}
                          onChange={(e) =>
                            handleInputChange("name", e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Tab') {
                              console.log('Tab key pressed on Name field - Raw form data:', formData);
                              
                              // Check if first 6 required fields have values (Member Number, Application No., Application Date, Registration Date, Title, Name)
                              const hasFirstSixFields =
                                formData.memberCode &&
                                formData.applicationId &&
                                formData.applicationDate &&
                                formData.regDate &&
                                formData.nameTitle &&
                                formData.name;

                              console.log('Tab key pressed on Name field - Debug info:', {
                                hasFirstSixFields,
                                isMemberSaved,
                                hasSubmittedFirstSeven,
                                individualFields: {
                                  memberCode: !!formData.memberCode,
                                  applicationId: !!formData.applicationId,
                                  ledgerNo: !!formData.ledgerNo,
                                  applicationDate: !!formData.applicationDate,
                                  regDate: !!formData.regDate,
                                  nameTitle: !!formData.nameTitle,
                                  name: !!formData.name
                                },
                                fieldValues: {
                                  memberCode: formData.memberCode,
                                  applicationId: formData.applicationId,
                                  ledgerNo: formData.ledgerNo,
                                  applicationDate: formData.applicationDate,
                                  regDate: formData.regDate,
                                  nameTitle: formData.nameTitle,
                                  name: formData.name
                                }
                              });

                              if (hasFirstSixFields && !isMemberSaved && !hasSubmittedFirstSeven) {
                                console.log('Tab key on Name: Submitting first six fields');
                                e.preventDefault();
                                setHasSubmittedFirstSeven(true);
                                handleFirstSevenFieldsSubmit();
                              } else {
                                console.log('Tab key on Name: Conditions not met for submission', {
                                  hasFirstSixFields,
                                  isMemberSaved,
                                  hasSubmittedFirstSeven
                                });
                              }
                            }
                          }}
                          disabled={isFormDisabled}
                          sx={textFieldStylePrimary}
                        />
                      </Grid>
                      <Grid item size={3}>
                        <Select
                          fullWidth
                          displayEmpty
                          size="small"
                          value={formData.gender || ""}
                          onChange={(e) =>
                            handleInputChange("gender", e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Tab') {
                              // Check if first 6 required fields have values and member is not saved
                              const hasFirstSixFields =
                                formData.memberCode &&
                                formData.applicationDate &&
                                formData.regDate &&
                                formData.nameTitle &&
                                formData.name &&
                                formData.gender;

                              if (hasFirstSixFields && !isMemberSaved && !hasSubmittedFirstSeven) {
                                e.preventDefault();
                                setHasSubmittedFirstSeven(true);
                                handleFirstSevenFieldsSubmit();
                              }
                            }
                          }}
                          disabled={!isMemberSaved}
                          sx={textFieldStylePrimary}
                          renderValue={(value) => value || "Gender"}
                        >
                          <MenuItem value="" disabled>
                            Select Gender
                          </MenuItem>
                          {(() => {
                            // Look for gender options in various possible groups
                            let genderOptions = optionsMap.GENDER || optionsMap['Gender'];
                            if (!genderOptions) {
                              const possibleGroups = ['GENDER', 'GENDER_TYPE', 'MEMBER_GENDER'];
                              for (const groupName of possibleGroups) {
                                if (optionsMap[groupName]) {
                                  genderOptions = optionsMap[groupName];
                                  break;
                                }
                              }
                            }

                            return genderOptions ? (
                              genderOptions.map((option) => (
                                <MenuItem key={option.id} value={option.text}>
                                  {option.text}
                                </MenuItem>
                              ))
                            ) : null;
                          })()}
                        </Select>
                      </Grid>
                    </Grid>

                    <Grid container size={12}>
                      <Grid item size={2}>
                        <Select
                          fullWidth
                          displayEmpty
                          size="small"
                          value={formData.soWoCo || ""}
                          onChange={(e) =>
                            handleInputChange("soWoCo", e.target.value)
                          }
                          disabled={!isMemberSaved}
                          sx={textFieldStylePrimary}
                          renderValue={(value) => value || "Relation"}
                        >
                          <MenuItem value="" disabled>
                            Select Relation
                          </MenuItem>
                          {(() => {
                            let sowoOptions = optionsMap.SOWOCO || optionsMap['SoWoCo'] || optionsMap.RELATION || optionsMap.Relation;
                            if (!sowoOptions) {
                              const possibleGroups = ['SOWOCO', 'SoWoCo', 'RELATION', 'Relation'];
                              for (const groupName of possibleGroups) {
                                if (optionsMap[groupName]) {
                                  sowoOptions = optionsMap[groupName];
                                  break;
                                }
                              }
                            }
                            return sowoOptions ? (
                              sowoOptions.map((option) => (
                                <MenuItem key={option.id || option.value} value={option.text}>
                                  {option.text}
                                </MenuItem>
                              ))
                            ) : null;
                          })()}
                        </Select>
                      </Grid>
                      <Grid item size={2}>
                        <Select
                          fullWidth
                          displayEmpty
                          size="small"
                          value={formData.fatherTitle || ""}
                          onChange={(e) =>
                            handleInputChange("fatherTitle", e.target.value)
                          }
                          disabled={!isMemberSaved}
                          sx={textFieldStylePrimary}
                          renderValue={(value) => value || "Title"}
                        >
                          <MenuItem value="" disabled>
                            Select Title
                          </MenuItem>
                          {(() => {
                            let fatherTitleOptions = optionsMap.FATHER_SALUTATION || optionsMap['Father Salutation'] || optionsMap.SALUTATION || optionsMap.Salutation;
                            if (!fatherTitleOptions) {
                              const possibleGroups = ['FATHER_SALUTATION', 'Father Salutation', 'SALUTATION', 'Salutation', 'TITLE', 'Title'];
                              for (const groupName of possibleGroups) {
                                if (optionsMap[groupName]) {
                                  fatherTitleOptions = optionsMap[groupName];
                                  break;
                                }
                              }
                            }
                            return fatherTitleOptions ? (
                              fatherTitleOptions.map((option) => (
                                <MenuItem key={option.id || option.value} value={option.text}>
                                  {option.text}
                                </MenuItem>
                              ))
                            ) : null;
                          })()}
                        </Select>
                      </Grid>
                      <Grid item size={8}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Father / Mother / Spouse Name"
                          value={formData.fatherName}
                          onChange={(e) => {
                            /* debug removed */
                            handleInputChange("fatherName", e.target.value);
                          }}

                          disabled={!isMemberSaved}
                          sx={textFieldStylePrimary}
                        />
                      </Grid>
                    </Grid>

                    <Grid container size={12}>
                      <Grid item size={3}>
                        <DateInput
                          fullWidth
                          label="DOB"
                          size="small"
                          value={formData.dob}
                          onChange={(value) => {
                            handleInputChange("dob", value);
                            const age = calculateAge(value);
                            handleInputChange("age", age);
                          }}
                          disabled={!isMemberSaved}
                          sx={textFieldStylePrimary}
                        />
                      </Grid>
                      <Grid item size={2}>
                        <TextField
                          fullWidth
                          label="Age"
                          type="number"
                          size="small"
                          value={formData.age}
                          onChange={(e) =>
                            handleInputChange("age", e.target.value)
                          }
                          inputProps={{ min: 0 }}

                          disabled={!isMemberSaved}
                          sx={textFieldStylePrimary}
                        />
                      </Grid>
                      <Grid item size={4}>
                        <TextField
                          fullWidth
                          label="Email"
                          type="email"
                          size="small"
                          value={formData.email}
                          onChange={(e) =>
                            handleInputChange("email", e.target.value)
                          }

                          disabled={!isMemberSaved}
                          sx={textFieldStylePrimary}
                        />
                      </Grid>
                      <Grid item size={3}>
                        <TextField
                          fullWidth
                          label="Mobile"
                          size="small"
                          value={formData.mobile}
                          onChange={(e) =>
                            handleInputChange("mobile", e.target.value)
                          }

                          disabled={!isMemberSaved}
                          sx={textFieldStylePrimary}
                        />
                      </Grid>
                    </Grid>
                    <Grid container size={12}>
                      {/* Alternate Contact No */}
                      <Grid item size={3}>
                        <TextField
                          fullWidth
                          label="Alternate Contact No"
                          size="small"
                          value={formData.alternateContactNo}
                          onChange={(e) =>
                            handleInputChange("alternateContactNo", e.target.value)
                          }
                          disabled={!isMemberSaved}
                          sx={textFieldStylePrimary}
                        />
                      </Grid>
                      <Grid item size={3}>
                        <Select
                          fullWidth
                          displayEmpty
                          size="small"
                          value={formData.religion || ""}
                          onChange={(e) =>
                            handleInputChange("religion", e.target.value)
                          }
                          disabled={!isMemberSaved}
                          sx={textFieldStylePrimary}
                          renderValue={(value) => value || "Religion"}
                        >
                          <MenuItem value="" disabled>
                            Select Religion
                          </MenuItem>
                          {(() => {
                            // Look for religion options in various possible groups
                            let religionOptions = optionsMap.RELIGION || optionsMap['Religion'];
                            if (!religionOptions) {
                              const possibleGroups = ['RELIGION', 'Religion'];
                              for (const groupName of possibleGroups) {
                                if (optionsMap[groupName]) {
                                  religionOptions = optionsMap[groupName];
                                  break;
                                }
                              }
                            }

                            return religionOptions ? (
                              religionOptions.map((option) => (
                                <MenuItem key={option.id || option.value} value={option.text}>
                                  {option.text}
                                </MenuItem>
                              ))
                            ) : null;
                          })()}
                        </Select>
                      </Grid>
                      <Grid item size={3}>
                        <Select
                          fullWidth
                          displayEmpty
                          size="small"
                          value={formData.maritalStatus || ""}
                          onChange={(e) =>
                            handleInputChange("maritalStatus", e.target.value)
                          }
                          disabled={!isMemberSaved}
                          sx={textFieldStylePrimary}
                          renderValue={(value) => value || "Marital Status"}
                        >
                          <MenuItem value="" disabled>
                            Select Marital Status
                          </MenuItem>
                          {(() => {
                            // Look for marital status options in various possible groups
                            let maritalStatusOptions = optionsMap.MARITAL_STATUS || optionsMap['Marital Status'];
                            if (!maritalStatusOptions) {
                              const possibleGroups = ['MARITAL_STATUS', 'MARITAL_STATUS_TYPE', 'MEMBER_MARITAL_STATUS'];
                              for (const groupName of possibleGroups) {
                                if (optionsMap[groupName]) {
                                  maritalStatusOptions = optionsMap[groupName];
                                  break;
                                }
                              }
                            }

                            return maritalStatusOptions ? (
                              maritalStatusOptions.map((option) => (
                                <MenuItem key={option.id || option.value} value={option.text || option.optionText}>
                                  {option.text}
                                </MenuItem>
                              ))
                            ) : null;
                          })()}
                        </Select>
                      </Grid>
                      <Grid item size={3}>
                        <DateInput
                          fullWidth
                          label="Marriage Date"
                          size="small"
                          value={formData.marriageDate}
                          onChange={(value) =>
                            handleInputChange("marriageDate", value)
                          }
                          disabled={!isMemberSaved}
                          sx={textFieldStylePrimary}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>


            {/* Addresses */}
            <Grid container size={12}>
              <Card variant="outlined" sx={{ width: '100%', backgroundColor: (theme) => theme.palette.mode === 'dark' ? `${theme.palette.success.main}08` : `${theme.palette.success.main}0A`, borderTop: `3px solid ${theme.palette.success.main}66` }}>
                <CardContent size={12} sx={{ minWidth: '100%' }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 2, color: theme.palette.success.main }}>
                    Addresses
                  </Typography>

                  {formData.addresses.map((address, index) => (
                    <Box key={index} sx={{ mb: index < formData.addresses.length - 1 ? 2 : 0, minWidth: '100%', width: '100%' }}>
                      {/* Address Header */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          p: 1,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          minWidth: '100%',
                          width: '100%',
                          backgroundColor: address.defaultAddress ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)') : 'transparent',
                          '&:hover': formData.addresses.length > 1 ? { boxShadow: 1 } : {}
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexGrow: 1,
                            cursor: formData.addresses.length > 1 ? 'pointer' : 'default'
                          }}
                          onClick={() => formData.addresses.length > 1 && toggleAddressExpansion(index)}
                        >
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {(() => {
                            if (!address.addressType) return 'Address';

                            // Look for the option text that matches the value
                            let addressOptions = optionsMap.ADDRESS_TYPE;
                            if (!addressOptions) {
                              const possibleGroups = ['ADDRESS', 'ADDRESS_TYPE', 'MEMBER_ADDRESS', 'ADDRESS_KIND'];
                              for (const groupName of possibleGroups) {
                                if (optionsMap[groupName]) {
                                  addressOptions = optionsMap[groupName];
                                  break;
                                }
                              }
                            }

                            // If still no address options, search all groups for address-related content
                            if (!addressOptions) {
                              for (const [groupName, options] of Object.entries(optionsMap)) {
                                if (options && options.some(opt =>
                                  opt.text && (
                                    opt.text.toLowerCase().includes('address') ||
                                    opt.text.toLowerCase().includes('residence') ||
                                    opt.text.toLowerCase().includes('permanent')
                                  )
                                )) {
                                  addressOptions = options;
                                  break;
                                }
                              }
                            }

                            // Find the matching option and return its text
                            if (addressOptions) {
                              const selectedOption = addressOptions.find(opt => opt.value === address.addressType);
                              if (selectedOption) return selectedOption.text;
                            }

                            // Fallback for residence
                            if (address.addressType === "residence") return "Residence Address";

                            // Fallback to capitalized value
                            return `${address.addressType.charAt(0).toUpperCase() + address.addressType.slice(1)} Address`;
                          })()}
                        </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={!!address.defaultAddress}
                                onChange={(e) => handleDefaultAddressChange(index, e.target.checked)}
                                onClick={(e) => e.stopPropagation()}
                                disabled={!isMemberSaved}
                                sx={{
                                  padding: 0.5,
                                  '&.Mui-checked': {
                                    color: colors.success[500],
                                  },
                                }}
                              />
                            }
                            label={address.defaultAddress ? "Default Address" : "Mark as Default"}
                            sx={{
                              mr: 1,
                              marginLeft: 0,
                              marginRight: 2,
                              '& .MuiTypography-root': {
                                fontSize: '0.8rem',
                                color: address.defaultAddress ? colors.success[500] : 'text.secondary',
                                fontWeight: address.defaultAddress ? 'bold' : 'normal'
                              }
                            }}
                          />

                          {formData.addresses.length > 1 && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeAddress(index);
                              }}
                              sx={{
                                color: 'error.main',
                                '&:hover': { backgroundColor: 'error.light', color: 'white' }
                              }}
                            >
                              <Close sx={{ fontSize: '16px' }} />
                            </IconButton>
                          )}
                          {formData.addresses.length > 1 && (
                            <IconButton size="small">
                              {expandedAddress === index ? (
                                <ExpandLess sx={{ fontSize: '16px' }} />
                              ) : (
                                <ExpandMore sx={{ fontSize: '16px' }} />
                              )}
                            </IconButton>
                          )}
                        </Box>
                      </Box>

                      {/* Address Form Fields */}
                      {expandedAddress === index && (
                        <Box sx={{ mt: 2 }}>
                          <Grid container spacing={1}>
                            <Grid item size={8}>
                              <TextField
                                label="Address"
                                fullWidth
                                size="small"
                                value={address.address || ""}
                                onChange={(e) => handleMemberAddressChange(e, index)}
                                rows={2}
                                maxRows={4}

                                disabled={!isMemberSaved}
                                sx={textFieldStyleSuccess}
                              />
                            </Grid>
                            <Grid item size={4}>
                              <Select
                                fullWidth
                                displayEmpty
                                size="small"
                                value={address.addressType || ""}
                                onChange={(e) =>
                                  handleAddressChange(index, "addressType", e.target.value)
                                }
                                disabled={!isMemberSaved}
                                sx={textFieldStyleSuccess}
                                renderValue={(value) => value || "Address Type"}
                              >
                                <MenuItem value="" disabled>
                                  Select Address Type
                                </MenuItem>
                                {(() => {
                                  // Look for address options in various possible groups
                                  let addressOptions = optionsMap.ADDRESS_TYPE || optionsMap['Address Type'];
                                  if (!addressOptions) {
                                    const possibleGroups = ['ADDRESS', 'ADDRESS_TYPE', 'MEMBER_ADDRESS', 'ADDRESS_KIND', 'Address Type'];
                                    for (const groupName of possibleGroups) {
                                      if (optionsMap[groupName]) {
                                        addressOptions = optionsMap[groupName];
                                        break;
                                      }
                                    }
                                  }

                                  // If still no address options, search all groups for address-related content
                                  if (!addressOptions) {
                                    for (const [groupName, options] of Object.entries(optionsMap)) {
                                      if (options && options.some(opt =>
                                        opt.text && (
                                          opt.text.toLowerCase().includes('address') ||
                                          opt.text.toLowerCase().includes('residence') ||
                                          opt.text.toLowerCase().includes('permanent')
                                        )
                                      )) {
                                        addressOptions = options;
                                        break;
                                      }
                                    }
                                  }

                                  return addressOptions ? (
                                    addressOptions.map((option) => (
                                      <MenuItem key={option.id} value={option.text}>
                                        {option.text}
                                      </MenuItem>
                                    ))
                                  ) : (
                                      <MenuItem value="Residence Address">Residence Address</MenuItem>
                                  );
                                })()}
                              </Select>
                            </Grid>

                            <Grid container size={12}>
                              <Grid item size={4}>
                                <TextField
                                  fullWidth
                                  label="Pincode"
                                  size="small"
                                  value={address.pincode || ""}
                                  onChange={(e) => {
                                    handleInputChange(`addresses.${index}.pincode`, e.target.value);
                                  }}
                                  disabled={!isMemberSaved}
                                  sx={textFieldStyleSuccess}
                                  InputProps={{
                                    endAdornment: isLoadingPincode && (
                                      <InputAdornment position="end">
                                        <Box
                                          sx={{
                                            width: 16,
                                            height: 16,
                                            border: '2px solid #f3f3f3',
                                            borderTop: '2px solid #3498db',
                                            borderRadius: '50%',
                                            animation: `${spin} 1s linear infinite`,
                                          }}
                                        />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                              <Grid item size={4}>
                                <TextField
                                  fullWidth
                                  label="State"
                                  size="small"
                                  value={address.state || ""}
                                  onChange={(e) =>
                                    handleAddressChange(index, "state", e.target.value)
                                  }
                                  disabled={!isMemberSaved}
                                  sx={textFieldStyleSuccess}
                                />
                              </Grid>
                              <Grid item size={4}>
                                <Select
                                  fullWidth
                                  displayEmpty
                                  size="small"
                                  value={address.district || ""}
                                  onChange={(e) => {
                                    const selectedDistrictName = e.target.value;
                                    const selectedDistrict = pincodeData[index]?.districts?.find(d => d.name === selectedDistrictName);
                                    handleAddressChange(index, "district", selectedDistrictName);
                                    handleAddressChange(index, "districtCode", selectedDistrict?.code || "");
                                    // Clear sub-district and village when district changes
                                    handleAddressChange(index, "subDistrict", "");
                                    handleAddressChange(index, "subDistrictCode", "");
                                    handleAddressChange(index, "village", "");
                                    handleAddressChange(index, "villageCode", "");
                                  }}
                                  disabled={!isMemberSaved}
                                  sx={textFieldStyleSuccess}
                                  renderValue={(value) => value || "District"}
                                >
                                  <MenuItem value="" disabled>
                                    Select District
                                  </MenuItem>
                                  {/* Fallback for pre-loaded district that hasn't been fetched via pincode yet */}
                                  {address.district && (!pincodeData[index]?.districts || !pincodeData[index].districts.some(d => d.name === address.district)) && (
                                    <MenuItem value={address.district}>
                                      {address.district}
                                    </MenuItem>
                                  )}
                                  {pincodeData[index]?.districts?.map((district) => (
                                    <MenuItem key={district.code} value={district.name}>
                                      {district.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </Grid>
                            </Grid>

                            <Grid container size={12}>
                              <Grid item size={4}>
                                <Autocomplete
                                  fullWidth
                                  size="small"
                                  freeSolo
                                  disableClearable
                                  options={pincodeData[index]?.subDistricts
                                    ?.filter(subDist =>
                                      !address.district ||
                                      pincodeData[index]?.districts?.find(d => d.name === address.district)?.code === subDist.districtCode
                                    )?.map(subDistrict => subDistrict.name) || []}
                                  value={address.subDistrict || ""}
                                  getOptionLabel={(option) => option || ""}
                                  onChange={(event, newValue) => {
                                    const selectedSubDistrictName = newValue || "";
                                    const selectedSubDistrict = pincodeData[index]?.subDistricts?.find(s => s.name === selectedSubDistrictName);
                                    handleAddressChange(index, "subDistrict", selectedSubDistrictName);
                                    handleAddressChange(index, "subDistrictCode", selectedSubDistrict?.code || "");
                                    // Clear village when sub-district changes
                                    handleAddressChange(index, "village", "");
                                    handleAddressChange(index, "villageCode", "");
                                  }}
                                  onInputChange={(event, newInputValue) => {
                                    handleAddressChange(index, "subDistrict", newInputValue);
                                    // Clear village when sub-district changes
                                    handleAddressChange(index, "village", "");
                                    handleAddressChange(index, "villageCode", "");
                                  }}
                                  disabled={!isMemberSaved}
                                  popupIcon={<KeyboardArrowDown />}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Sub-District"
                                      placeholder="Select or enter Sub-District"
                                      sx={textFieldStyleSuccess}
                                    />
                                  )}
                                />
                              </Grid>
                              <Grid item size={8}>
                                <Autocomplete
                                  fullWidth
                                  size="small"
                                  freeSolo
                                  disableClearable
                                  options={pincodeData[index]?.villages
                                    ?.filter(village =>
                                      !address.subDistrict ||
                                      pincodeData[index]?.subDistricts?.find(s => s.name === address.subDistrict)?.code === village.subDistrictCode
                                    )?.map(village => village.name) || []}
                                  value={address.village || ""}
                                  getOptionLabel={(option) => option || ""}
                                  onChange={(event, newValue) => {
                                    const selectedVillageName = newValue || "";
                                    const selectedVillage = pincodeData[index]?.villages?.find(v => v.name === selectedVillageName);
                                    handleAddressChange(index, "village", selectedVillageName);
                                    handleAddressChange(index, "villageCode", selectedVillage?.code || "");
                                  }}
                                  onInputChange={(event, newInputValue) => {
                                    handleAddressChange(index, "village", newInputValue);
                                  }}
                                  disabled={!isMemberSaved}
                                  popupIcon={<KeyboardArrowDown />}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Village"
                                      placeholder="Select or enter Village"
                                      sx={textFieldStyleSuccess}
                                    />
                                  )}
                                />
                              </Grid>
                            </Grid>

                            <Grid container size={12}>
                              <Grid item size={3}>
                                <Select
                                  value={address.houseStatus || ""}
                                  fullWidth
                                  size="small"
                                  onChange={(e) =>
                                    handleAddressChange(index, "houseStatus", e.target.value)
                                  }
                                  disabled={!isMemberSaved}
                                  disableUnderline
                                  displayEmpty
                                  renderValue={(value) => {
                                    if (!value) return "Select Ownership";
                                    return value === "owned" ? "Owned" : value === "rented" ? "Rented" : value;
                                  }}
                                  sx={textFieldStyleSuccess}
                                >
                                  <MenuItem value="" disabled>
                                    Select Ownership
                                  </MenuItem>
                                  <MenuItem value="owned">Owned</MenuItem>
                                  <MenuItem value="rented">Rented</MenuItem>
                                </Select>
                              </Grid>
                              <Grid item size={6}>
                                <TextField
                                  fullWidth
                                  label="C/O"
                                  size="small"
                                  value={address.careOfName || ""}
                                  onChange={(e) =>
                                    handleAddressChange(index, "careOfName", e.target.value)
                                  }
                                  disabled={!isMemberSaved}
                                  sx={textFieldStyleSuccess}
                                />
                              </Grid>
                              <Grid item size={3}>
                                <TextField
                                  fullWidth
                                  label="Contact No."
                                  size="small"
                                  value={address.contactNo || ""}
                                  onChange={(e) =>
                                    handleAddressChange(index, "contactNo", e.target.value)
                                  }
                                  disabled={!isMemberSaved}
                                  sx={textFieldStyleSuccess}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </Box>
                  ))}

                  {/* Add Address Button at Bottom */}
                  <Box sx={{ display: 'flex', justifyContent: 'end', mt: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Add />}
                      onClick={addNewAddress}
                      sx={{
                        minWidth: 'auto',
                        px: 2,
                        py: 1,
                        fontSize: '0.75rem'
                      }}
                    >
                      Add Address
                    </Button>
                  </Box>
                </CardContent>
              </Card>

              {/* Office Details */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? `${theme.palette.warning.main}08` : `${theme.palette.warning.main}0A`, borderTop: `3px solid ${theme.palette.warning.main}66` }}>
                  <CardContent>
                    <Grid container size={12} sx={{ mb: 1, color: theme.palette.warning.main }} >
                      <Grid item size={6}>
                        <Typography variant="h6" gutterBottom>
                          Office Details
                        </Typography>
                      </Grid>
                      <Grid size={6}>
                        <Select
                          value={formData.reportingManager}
                          onChange={(e) =>
                            handleInputChange(
                              "reportingManager",
                              e.target.value
                            )
                          }
                          fullWidth
                          size="small"
                          disabled={!isMemberSaved}
                          disableUnderline
                          displayEmpty
                          renderValue={(value) => {
                            if (!value) return "Select Disbursing Officer";
                            // Try to find the text for the selected value
                            let disbursingOptions = optionsMap['Disbursing Officer'];
                            if (!disbursingOptions) {
                              // Fallback search
                              const possibleGroups = ['Disbursing Officer', 'DISBURSING_OFFICER', 'Reporting Manager', 'REPORTING_MANAGER'];
                              for (const groupName of possibleGroups) {
                                if (optionsMap[groupName]) {
                                  disbursingOptions = optionsMap[groupName];
                                  break;
                                }
                              }
                            }
                            const selectedOption = disbursingOptions?.find(opt => opt.value === value || opt.optionValue === value || opt.text === value);
                            return selectedOption ? (selectedOption.text || selectedOption.optionText) : value;
                          }}
                          sx={textFieldStyleWarning}
                        >
                          <MenuItem value="" disabled>
                            Select Disbursing Officer
                          </MenuItem>
                          {(() => {
                            let disbursingOptions = optionsMap['Disbursing Officer'];
                            if (!disbursingOptions) {
                              const possibleGroups = ['Disbursing Officer', 'DISBURSING_OFFICER', 'Reporting Manager', 'REPORTING_MANAGER'];
                              for (const groupName of possibleGroups) {
                                if (optionsMap[groupName]) {
                                  disbursingOptions = optionsMap[groupName];
                                  break;
                                }
                              }
                            }

                            return disbursingOptions ? (
                              disbursingOptions.map((option) => (
                                <MenuItem
                                  key={option.id || option.value || option.optionValue}
                                  value={option.text || option.optionText}
                                >
                                  {option.text || option.optionText}
                                </MenuItem>
                                ))
                            ) : (
                              <MenuItem disabled>No options available</MenuItem>
                            );
                          })()}
                        </Select>
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid container size={12}>
                        <Grid item size={12}>
                          <TextField
                            fullWidth
                            label="Office Name"
                            size="small"
                            value={formData.companyName}
                            onChange={(e) =>
                              handleInputChange("companyName", e.target.value)
                            }

                            disabled={!isMemberSaved}
                            sx={textFieldStyleWarning}
                          />
                        </Grid>
                        <Grid item size={12}>
                          <TextField
                            fullWidth
                            label="Corporate / Head Office Address"
                            size="small"
                            value={formData.headOfficeAddress}
                            onChange={(e) =>
                              handleInputChange(
                                "headOfficeAddress",
                                e.target.value
                              )
                            }
                            // multiline
                            rows={2}
                            maxRows={4}
                            padding={1}
                            disabled={!isMemberSaved}
                            sx={textFieldStyleWarning}
                          />
                        </Grid>
                        <Grid item size={12}>
                          <TextField
                            fullWidth
                            label="Client Office / Posting Address"
                            size="small"
                            value={formData.officePostingAddress}
                            onChange={(e) =>
                              handleInputChange("officePostingAddress", e.target.value)
                            }
                            // multiline
                            rows={2}
                            maxRows={4}
                            disabled={!isMemberSaved}
                            sx={textFieldStyleWarning}
                          />
                        </Grid>
                      </Grid>
                      <Grid container size={12}>
                        <Grid item size={4}>
                          <Select
                            fullWidth
                            displayEmpty
                            size="small"
                            value={formData.occupation || ""}
                            onChange={(e) =>
                              handleInputChange("occupation", e.target.value)
                            }
                            disabled={!isMemberSaved}
                            sx={textFieldStylePrimary}
                            renderValue={(value) => {
                              if (!value) return "Occupation";
                              // Since we're now saving plain text, just return the value
                              return value;
                            }}
                          >
                            <MenuItem value="" disabled>
                              Select Occupation
                            </MenuItem>
                            {(() => {
                              // Look for occupation options in various possible groups
                              let occupationOptions = optionsMap.OCCUPATION || optionsMap['Occupation'];
                              if (!occupationOptions) {
                                const possibleGroups = ['OCCUPATION', 'OCCUPATION_TYPE', 'MEMBER_OCCUPATION'];
                                for (const groupName of possibleGroups) {
                                  if (optionsMap[groupName]) {
                                    occupationOptions = optionsMap[groupName];
                                    break;
                                  }
                                }
                              }

                              return occupationOptions ? (
                                occupationOptions.map((option) => (
                                  <MenuItem key={option.id} value={option.text}>
                                    {option.text}
                                  </MenuItem>
                                ))
                              ) : null;
                            })()}
                          </Select>
                        </Grid>
                        <Grid item size={4}>
                          <TextField
                            fullWidth
                            label="Employee Code"
                            size="small"
                            value={formData.employeeId}
                            onChange={(e) =>
                              handleInputChange("employeeId", e.target.value)
                            }

                            disabled={!isMemberSaved}
                            sx={textFieldStyleWarning}
                          />
                        </Grid>
                        <Grid item size={4}>
                          <TextField
                            fullWidth
                            label="Designation"
                            size="small"
                            value={formData.designation}
                            onChange={(e) =>
                              handleInputChange("designation", e.target.value)
                            }
                            disabled={!isMemberSaved}
                            sx={textFieldStyleWarning}
                          />
                        </Grid>
                        <Grid item size={4}>
                          <TextField
                            fullWidth
                            label="Department"
                            size="small"
                            value={formData.department}
                            onChange={(e) =>
                              handleInputChange("department", e.target.value)
                            }
                            disabled={!isMemberSaved}
                            sx={textFieldStyleWarning}
                          />
                        </Grid>
                        <Grid item size={4}>
                          <TextField
                            fullWidth
                            label="Telephone No"
                            size="small"
                            value={formData.telephoneNo}
                            onChange={(e) =>
                              handleInputChange("telephoneNo", e.target.value)
                            }
                            disabled={!isMemberSaved}
                            sx={textFieldStyleWarning}
                          />
                        </Grid>
                        <Grid item size={4}>
                          <TextField
                            fullWidth
                            label="Income"
                            size="small"
                            value={formData.income}
                            onChange={(e) =>
                              handleInputChange("income", e.target.value)
                            }
                            disabled={!isMemberSaved}
                            sx={textFieldStyleWarning}
                          />
                        </Grid>
                      </Grid>

                      <Grid container size={12}>

                        <Grid item size={4}>
                          <TextField
                            fullWidth
                            label="PF No."
                            size="small"
                            value={formData.pfGpfNo}
                            onChange={(e) =>
                              handleInputChange("pfGpfNo", e.target.value)
                            }
                            disabled={!isMemberSaved}
                            sx={textFieldStyleWarning}
                          />
                        </Grid>
                        <Grid item size={4}>
                          <DateInput
                            fullWidth
                            label="Retirement Date"
                            size="small"
                            value={formData.officeJoiningDate}
                            onChange={(value) =>
                              handleInputChange("officeJoiningDate", value)
                            }
                            disabled={!isMemberSaved}
                            sx={textFieldStyleWarning}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Agent and Introducer Details */}
              <Card variant="outlined" sx={{ width: '100%', backgroundColor: (theme) => theme.palette.mode === 'dark' ? `${theme.palette.secondary.main}08` : `${theme.palette.secondary.main}0A`, borderTop: `3px solid ${theme.palette.secondary.main}66` }}>
                  <CardContent size={12} sx={{ minWidth: '100%' }}>

                    {/* Agent Details */}
                    <Grid container size={12} spacing={1}>
                      <Grid item size={8}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            flexDirection: "column",
                            alignItems: "start",
                            justifyContent: "start",
                            width: "100%",
                          }}
                        >
                          <Box sx={{ position: 'relative', width: '100%' }}>
                            <TextField
                              fullWidth
                              label="Agent Code"
                              size="small"
                              value={formData.agentCode}
                              onChange={handleAgentCodeChange}
                              onFocus={() => {
                                // Always show all options when focused
                                filterAgentSuggestions(formData.agentCode || '');
                              }}
                              onClick={() => {
                                // Show all options when clicked
                                showAllAgentOptions();
                              }}
                              onBlur={() => {
                                // Delay hiding suggestions to allow click on suggestion
                                setTimeout(() => {
                                  setShowAgentSuggestions(false);
                                }, 200);
                              }}
                              disabled={!isMemberSaved}
                            sx={textFieldStyleSecondary}
                            />

                            {/* Autocomplete Suggestions Dropdown */}
                            {showAgentSuggestions && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: 0,
                                  right: 0,
                                  zIndex: 1000,
                                  backgroundColor: 'white',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                  maxHeight: '200px',
                                  overflowY: 'auto',
                                  mt: 0.5
                                }}
                              >
                                {agentSuggestions.length > 0 ? (
                                  agentSuggestions.map((option, index) => (
                                    <Box
                                      key={option.id || index}
                                      onClick={() => handleAgentSuggestionSelect(option)}
                                      sx={{
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                        borderBottom: index < agentSuggestions.length - 1 ? '1px solid #eee' : 'none',
                                        '&:hover': {
                                          backgroundColor: '#f5f5f5'
                                        }
                                      }}
                                    >
                                      <Typography variant="body2">
                                        {option.text}
                                      </Typography>
                                    </Box>
                                  ))
                                ) : (
                                  <Box
                                    sx={{
                                      padding: '8px 12px',
                                      color: '#666',
                                      fontStyle: 'italic'
                                    }}
                                  >
                                    <Typography variant="body2">
                                      No matches found
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item size={4}>
                        <DateInput
                          fullWidth
                          label="Agent Date"
                          size="small"
                          value={formData.leadDate}
                          onChange={(value) =>
                            handleInputChange("leadDate", value)
                          }
                          disabled={!isMemberSaved}
                        sx={textFieldStyleSecondary}
                        />
                      </Grid>
                    </Grid>

                    {/* Introducer Details */}
                    <Grid container size={12} spacing={1} mt={1}>
                    <Grid item size={4}>
                        <TextField
                          fullWidth
                        label="Introducer (No. or Name)"
                          size="small"
                        value={formData.introducer}
                          onChange={(e) =>
                            handleInputChange(
                              "introducer",
                              e.target.value
                            )
                          }
                        onBlur={(e) => handleIntroducerChange(e.target.value)}
                          disabled={!isMemberSaved}
                        InputLabelProps={{ shrink: !!formData.introducer }}
                          sx={textFieldStyleSecondary}
                          InputProps={{
                            endAdornment: isLoadingIntroducer ? (
                              <Box
                                sx={{
                                  width: 16,
                                  height: 16,
                                  border: "2px solid #ccc",
                                  borderTop: "2px solid transparent",
                                  borderRadius: "50%",
                                  animation: `${spin} 1s linear infinite`,
                                }}
                              />
                            ) : null
                          }}
                        />
                    </Grid>
                    <Grid item size={3}>
                      <TextField
                        fullWidth
                        label="Mobile No"
                          size="small"
                        value={formData.introducerMobile}
                        onChange={(e) =>
                          handleInputChange(
                            "introducerMobile",
                            e.target.value
                          )
                        }
                        disabled={!isMemberSaved}
                        InputLabelProps={{ shrink: !!formData.introducerMobile }}
                        sx={textFieldStyleSecondary}
                      />
                    </Grid>
                    <Grid item size={4}>
                      <Select
                        fullWidth
                        displayEmpty
                        size="small"
                        value={formData.introducerRelation || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "introducerRelation",
                            e.target.value
                          )
                        }
                        disabled={!isMemberSaved}
                        sx={textFieldStyleSecondary}
                        renderValue={(value) => value || "Select Relation"}
                      >
                        <MenuItem value="" disabled>
                          Select Relation
                        </MenuItem>
                        {(() => {
                          let relationOptions = optionsMap.RELATION || optionsMap['Relation'] || optionsMap.NOMINEE_RELATION || optionsMap['Nominee Relation'] || optionsMap.MEMBER_RELATION;
                          if (!relationOptions) {
                            const possibleGroups = ['RELATION', 'Relation', 'NOMINEE_RELATION', 'Nominee Relation', 'MEMBER_RELATION'];
                            for (const groupName of possibleGroups) {
                              if (optionsMap[groupName]) {
                                relationOptions = optionsMap[groupName];
                                break;
                              }
                            }
                          }

                          return relationOptions ? (
                            relationOptions.map((option) => (
                              <MenuItem key={option.id || option.value} value={option.text || option.optionText}>
                                {option.text || option.optionText}
                              </MenuItem>
                            ))
                          ) : null;
                        })()}
                      </Select>
                      </Grid>
                      <Grid item size={1}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={addIntroducerToList}
                        disabled={!isMemberSaved || !formData.introducer} // Require at least a name
                          sx={{
                            minWidth: 'auto',
                            width: '100%',
                            height: '40px',
                            backgroundColor: colors.primary[500],
                            '&:hover': {
                              backgroundColor: colors.primary[600],
                            },
                            '&:disabled': {
                              backgroundColor: '#ccc',
                              color: '#666'
                            }
                          }}
                        >
                          <Add sx={{ fontSize: '20px' }} />
                        </Button>
                      </Grid>
                    </Grid>

                    {/* Introducer Table */}
                    {introducerList.length > 0 && (
                      <Box sx={{ mt: 2, width: '100%' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Introducer List
                        </Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 200 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                No.
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                Name
                              </TableCell>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                Mobile
                              </TableCell>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                Relation
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '60px' }}>
                                  Action
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {introducerList.map((introducer, index) => (
                                <TableRow key={index}>
                                  <TableCell>{introducer.introducerNo}</TableCell>
                                  <TableCell>{introducer.introducerName}</TableCell>
                                  <TableCell>{introducer.introducerMobile}</TableCell>
                                  <TableCell>{introducer.introducerRelation}</TableCell>
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      onClick={() => removeIntroducerFromList(index)}
                                      sx={{
                                        color: 'error.main',
                                        '&:hover': {
                                          backgroundColor: 'error.light',
                                          color: 'white'
                                        }
                                      }}
                                    >
                                      <Close sx={{ fontSize: '16px' }} />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                  </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* KYC and Bank Details */}
          <Grid container xs={12} md={6} spacing={2} sx={{ alignSelf: 'flex-start' }}>
            <Card variant="outlined" sx={{ height: 'fit-content', width: '100%', backgroundColor: (theme) => theme.palette.mode === 'dark' ? `${theme.palette.error.main}08` : `${theme.palette.error.main}0A`, borderTop: `3px solid ${theme.palette.error.main}66` }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: theme.palette.error.main }} gutterBottom>
                  KYC and Bank Details
                </Typography>
                <Grid container spacing={1}>
                  <Grid container size={12}>
                    {/* Aadhar */}
                    <Grid item size={6}>
                      <Box sx={{ width: "100%" }}>
                        <TextField
                          fullWidth
                          label="Aadhaar Number"
                          size="small"
                          value={formData.aadharNumber || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "aadharNumber",
                              e.target.value
                            )
                          }

                          disabled={!isMemberSaved}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment
                                position="end"
                                sx={{ margin: 0 }}
                              >
                                <Button
                                  onClick={() =>
                                    handleOpenFileUpload(
                                      "aadharFiles",
                                      "aadhar files"
                                    )
                                  }
                                  variant="contained"
                                  size="small"
                                  disabled={uploadingFiles["aadharFiles"] || !isMemberSaved}
                                  sx={{
                                    fontSize: "0.7rem",
                                    textTransform: "none",
                                    minWidth: "auto",
                                    color: "white",
                                    px: 1,
                                    height: "100%",
                                    backgroundColor: (() => {
                                      const hasFiles = formData.aadharFiles && formData.aadharFiles.length > 0;
                                      return hasFiles ? colors.success[500] : colors.error[500];
                                    })(),
                                    borderTopLeftRadius: 0,
                                    borderBottomLeftRadius: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  {uploadingFiles["aadharFiles"] ? (
                                    <Box
                                      sx={{
                                        width: 16,
                                        height: 16,
                                        border: "2px solid white",
                                        borderTop: "2px solid transparent",
                                        borderRadius: "50%",
                                        animation: `${spin} 1s linear infinite`,
                                      }}
                                    />
                                  ) : (
                                    <CloudUpload
                                      sx={{ fontSize: "1.5rem" }}
                                    />
                                  )}
                                </Button>
                              </InputAdornment>
                            ),
                            sx: {
                              paddingRight: 0,
                            },
                          }}
                          sx={{
                            ...textFieldStyleError,
                            "& .MuiOutlinedInput-root": {
                              paddingRight: 0,
                            },
                          }}
                        />
                      </Box>
                    </Grid>

                    {/* PAN */}
                    <Grid item size={6}>
                      <Box sx={{ width: "100%" }}>
                        <TextField
                          fullWidth
                          label="PAN Number"
                          size="small"
                          value={formData.panNumber || ""}
                          onChange={(e) =>
                            handleInputChange("panNumber", e.target.value)
                          }

                          disabled={!isMemberSaved}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment
                                position="end"
                                sx={{ margin: 0 }}
                              >
                                <Button
                                  onClick={() =>
                                    handleOpenFileUpload(
                                      "panFiles",
                                      "PAN Number"
                                    )
                                  }
                                  variant="contained"
                                  size="small"
                                  disabled={!isMemberSaved}
                                  sx={{
                                    fontSize: "0.7rem",
                                    textTransform: "none",
                                    minWidth: "auto",
                                    color: "white",
                                    px: 1,
                                    height: "100%",
                                      backgroundColor: (() => {
                                        const hasFiles = formData.panFiles && formData.panFiles.length > 0;
                                        return hasFiles ? colors.success[500] : colors.error[500];
                                      })(),
                                    borderTopLeftRadius: 0,
                                    borderBottomLeftRadius: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <CloudUpload
                                    sx={{ fontSize: "1.5rem" }}
                                  />
                                </Button>
                              </InputAdornment>
                            ),
                            sx: {
                              paddingRight: 0,
                            },
                          }}
                          sx={{
                            ...textFieldStyleError,
                            "& .MuiOutlinedInput-root": {
                              paddingRight: 0,
                            },
                          }}
                        />
                      </Box>
                    </Grid>

                    <Grid container size={12}>
                      {/* NSO */}
                      <Grid item size={6}>
                        <Box sx={{ width: "100%" }}>
                          <TextField
                            fullWidth
                            label="Passport Number"
                            size="small"
                            value={formData.passportDlNo || ""}
                            onChange={(e) =>
                              handleInputChange("passportDlNo", e.target.value)
                            }

                            disabled={!isMemberSaved}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment
                                  position="end"
                                  sx={{ margin: 0 }}
                                >
                                  <Button
                                    onClick={() =>
                                      handleOpenFileUpload(
                                        "passportFiles",
                                        "Passport Number"
                                      )
                                    }
                                    variant="contained"
                                    size="small"
                                    disabled={!isMemberSaved}
                                    sx={{
                                      fontSize: "0.7rem",
                                      textTransform: "none",
                                      minWidth: "auto",
                                      color: "white",
                                      px: 1,
                                      height: "100%",
                                      backgroundColor:
                                        formData.passportFiles &&
                                          formData.passportFiles.length > 0
                                          ? colors.success[500]
                                          : colors.error[500],
                                      borderTopLeftRadius: 0,
                                      borderBottomLeftRadius: 0,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <CloudUpload
                                      sx={{ fontSize: "1.5rem" }}
                                    />
                                  </Button>
                                </InputAdornment>
                              ),
                              sx: {
                                paddingRight: 0,
                              },
                            }}
                            sx={{
                              ...textFieldStyleError,
                              "& .MuiOutlinedInput-root": {
                                paddingRight: 0,
                              },
                            }}
                          />
                        </Box>
                      </Grid>

                      {/* VID */}
                      <Grid item size={6}>
                        <Box sx={{ width: "100%" }}>
                          <TextField
                            fullWidth
                            label="Voter ID Number"
                            size="small"
                            value={formData.voterRationCardNo || ""}
                            onChange={(e) =>
                              handleInputChange("voterRationCardNo", e.target.value)
                            }

                            disabled={!isMemberSaved}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment
                                  position="end"
                                  sx={{ margin: 0 }}
                                >
                                  <Button
                                    onClick={() =>
                                      handleOpenFileUpload(
                                        "vidFiles",
                                        "Voter ID Number"
                                      )
                                    }
                                    variant="contained"
                                    size="small"
                                    disabled={!isMemberSaved}
                                    sx={{
                                      fontSize: "0.7rem",
                                      textTransform: "none",
                                      minWidth: "auto",
                                      color: "white",
                                      px: 1,
                                      height: "100%",
                                      backgroundColor:
                                        formData.vidFiles &&
                                          formData.vidFiles.length > 0
                                          ? colors.success[500]
                                          : colors.error[500],
                                      borderTopLeftRadius: 0,
                                      borderBottomLeftRadius: 0,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <CloudUpload
                                      sx={{ fontSize: "1.5rem" }}
                                    />
                                  </Button>
                                </InputAdornment>
                              ),
                              sx: {
                                paddingRight: 0,
                              },
                            }}
                            sx={{
                              ...textFieldStyleError,
                              "& .MuiOutlinedInput-root": {
                                paddingRight: 0,
                              },
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>

                    <Grid container size={12}>
                      {/* CA */}
                      <Grid item size={6}>
                        <Box sx={{ width: "100%" }}>
                          <TextField
                            fullWidth
                            label="Elec Bill Number"
                            size="small"
                            value={formData.electricityBill || ""}
                            onChange={(e) =>
                              handleInputChange("electricityBill", e.target.value)
                            }

                            disabled={!isMemberSaved}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment
                                  position="end"
                                  sx={{ margin: 0 }}
                                >
                                  <Button
                                    onClick={() =>
                                      handleOpenFileUpload(
                                        "caFiles",
                                        "Electricity Bill Number"
                                      )
                                    }
                                    variant="contained"
                                    size="small"
                                    disabled={!isMemberSaved}
                                    sx={{
                                      fontSize: "0.7rem",
                                      textTransform: "none",
                                      minWidth: "auto",
                                      color: "white",
                                      px: 1,
                                      height: "100%",
                                      backgroundColor:
                                        formData.caFiles &&
                                          formData.caFiles.length > 0
                                          ? colors.success[500]
                                          : colors.error[500],
                                      borderTopLeftRadius: 0,
                                      borderBottomLeftRadius: 0,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <CloudUpload
                                      sx={{ fontSize: "1.5rem" }}
                                    />
                                  </Button>
                                </InputAdornment>
                              ),
                              sx: {
                                paddingRight: 0,
                              },
                            }}
                            sx={{
                              ...textFieldStyleError,
                              "& .MuiOutlinedInput-root": {
                                paddingRight: 0,
                              },
                            }}
                          />
                        </Box>
                      </Grid>
                      {/* KYC Date */}
                      <Grid item size={6}>
                        <DateInput
                          fullWidth
                          label="KYC Date"
                          size="small"
                          value={formData.kycVerificationDate}
                          onChange={(value) =>
                            handleInputChange("kycVerificationDate", value)
                          }
                          disabled={!isMemberSaved}
                          sx={textFieldStyleError}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Bank Details */}
                  <Grid container size={12}>
                    <Grid item size={4}>
                      <TextField
                        fullWidth
                        label="Account No."
                        size="small"
                        value={formData.bankAccountNo}
                        onChange={(e) =>
                          handleInputChange("bankAccountNo", e.target.value)
                        }
                        disabled={!isMemberSaved}
                        sx={textFieldStyleError}
                      />
                    </Grid>
                    <Grid item size={8}>
                      <Box sx={{ position: 'relative', width: '100%' }}>
                        <TextField
                          fullWidth
                          label="Bank Name"
                          size="small"
                          value={formData.bankName}
                          onChange={handleBankNameChange}
                          onFocus={() => {
                            // Always show all options when focused
                            filterBankSuggestions(formData.bankName || '');
                          }}
                          onClick={() => {
                            // Show all options when clicked
                            showAllBankOptions();
                          }}
                          onBlur={() => {
                            // Delay hiding suggestions to allow click on suggestion
                            setTimeout(() => {
                              setShowBankSuggestions(false);
                            }, 200);
                          }}
                          disabled={!isMemberSaved}
                          sx={textFieldStyleError}
                        />
                        
                        {/* Autocomplete Suggestions Dropdown */}
                        {showBankSuggestions && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              zIndex: 1000,
                              backgroundColor: 'white',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              maxHeight: '200px',
                              overflowY: 'auto',
                              mt: 0.5
                            }}
                          >
                            {bankSuggestions.length > 0 ? (
                              bankSuggestions.map((option, index) => (
                                <Box
                                  key={option.id || index}
                                  onClick={() => handleBankSuggestionSelect(option)}
                                  sx={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    borderBottom: index < bankSuggestions.length - 1 ? '1px solid #eee' : 'none',
                                    '&:hover': {
                                      backgroundColor: '#f5f5f5'
                                    }
                                  }}
                                >
                                  <Typography variant="body2">
                                    {option.text}
                                  </Typography>
                                </Box>
                              ))
                            ) : (
                              <Box
                                sx={{
                                  padding: '8px 12px',
                                  color: '#666',
                                  fontStyle: 'italic'
                                }}
                              >
                                <Typography variant="body2">
                                  No matches found
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  </Grid>

                  <Grid container size={12}>
                    <Grid item size={4}>
                      <TextField
                        fullWidth
                        label="Bank Branch"
                        size="small"
                        value={formData.bankBranch}
                        onChange={(e) =>
                          handleInputChange("bankBranch", e.target.value)
                        }
                        disabled={!isMemberSaved}
                        sx={textFieldStyleError}
                      />
                    </Grid>
                    <Grid item size={4}>
                      <TextField
                        fullWidth
                        label="IFSC Code"
                        size="small"
                        value={formData.ifscCode}
                        onChange={(e) =>
                          handleInputChange("ifscCode", e.target.value)
                        }
                        disabled={!isMemberSaved}
                        sx={textFieldStyleError}
                      />
                    </Grid>
                    <Grid item size={4}>
                      <TextField
                        fullWidth
                        label="Bank Manager"
                        size="small"
                        value={formData.bankManager}
                        onChange={(e) =>
                          handleInputChange("bankManager", e.target.value)
                        }
                        disabled={!isMemberSaved}
                        sx={textFieldStyleError}
                      />
                    </Grid>
                  </Grid>

                  <Grid item size={12}>
                    <TextField
                      label="Bank Address"
                      fullWidth
                      size="small"
                      value={formData.bankAddress}
                      onChange={(e) =>
                        handleInputChange("bankAddress", e.target.value)
                      }
                      // multiline
                      rows={2}
                      maxRows={4}
                      disabled={!isMemberSaved}
                      sx={textFieldStyleError}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          flexDirection: { xs: "column", sm: "row" },
                          alignItems: { xs: "center", sm: "center" },
                          justifyContent: { xs: "center", sm: "end" },
                          width: "100%",
                        }}
                      >
                        {/* Photo & Signature - Square and Centered */}
                        <Box
                          sx={{
                            width: { xs: "100px", sm: "120px" },
                            height: { xs: "120px", sm: "150px" },
                            "& .MuiBox-root": {
                              width: { xs: "100px !important", sm: "120px !important" },
                              height: { xs: "120px !important", sm: "150px !important" },
                              aspectRatio: "1 !important",
                            }
                          }}
                        >
                          <ImageUploadField
                            label="Photograph"
                            field="photographFile"
                            maxFiles={1}
                            files={formData.photographFile || []}
                            onFileUpload={handleFileUpload}
                            onFileRemove={handleFileRemove}
                          />
                        </Box>
                        <Box
                          sx={{
                            width: { xs: "100px", sm: "120px" },
                            height: { xs: "120px", sm: "150px" },
                            "& .MuiBox-root": {
                              width: { xs: "100px !important", sm: "120px !important" },
                              height: { xs: "120px !important", sm: "150px !important" },
                              aspectRatio: "1 !important",
                            }
                          }}
                        >
                          <ImageUploadField
                            label="Signature"
                            field="signatureFile"
                            maxFiles={1}
                            files={formData.signatureFile || []}
                            onFileUpload={handleFileUpload}
                            onFileRemove={handleFileRemove}
                          />
                        </Box>
                      </Box>
                    </Grid>
                </Grid>
              </CardContent>
            </Card>
            {/* Nominee Details */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? `${colors.accent[500]}08` : `${colors.accent[500]}0A`, borderTop: `3px solid ${colors.accent[500]}66` }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: colors.accent[700] }} gutterBottom>
                    Nominee Details
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid container size={12}>
                      <Grid item size={2}>
                        <Select
                          value={formData.nomineeTitle}
                          sx={textFieldStyleAccent}
                          size="small"
                          onChange={(e) =>
                            handleInputChange("nomineeTitle", e.target.value)
                          }
                          disabled={!isMemberSaved}
                          disableUnderline
                        >
                          <MenuItem value="Mr">Mr</MenuItem>
                          <MenuItem value="Mrs">Mrs</MenuItem>
                          <MenuItem value="Ms">Ms</MenuItem>
                          <MenuItem value="Dr">Dr</MenuItem>
                        </Select>
                      </Grid>
                      <Grid item size={6}>
                        <TextField
                          //  size={9}
                          fullWidth
                          label="Name"
                          size="small"
                          value={formData.nomineeName}
                          onChange={(e) =>
                            handleInputChange("nomineeName", e.target.value)
                          }

                          disabled={!isMemberSaved}
                          sx={textFieldStyleAccent}
                        />
                      </Grid>
                      <Grid item size={4}>
                        <Select
                          fullWidth
                          displayEmpty
                          size="small"
                          value={formData.nomineeRelation || ""}
                          onChange={(e) =>
                            handleInputChange("nomineeRelation", e.target.value)
                          }
                          disabled={!isMemberSaved}
                          sx={textFieldStyleAccent}
                          renderValue={(value) => value || "Select Relation"}
                        >
                          <MenuItem value="" disabled>
                            Select Relation
                          </MenuItem>
                          {(() => {
                            // Try to find relation options in multiple possible groups
                            let relationOptions = optionsMap.RELATION || optionsMap['Relation'] || optionsMap.NOMINEE_RELATION || optionsMap['Nominee Relation'] || optionsMap.MEMBER_RELATION;
                            if (!relationOptions) {
                              const possibleGroups = ['RELATION', 'Relation', 'NOMINEE_RELATION', 'Nominee Relation', 'MEMBER_RELATION'];
                              for (const groupName of possibleGroups) {
                                if (optionsMap[groupName]) {
                                  relationOptions = optionsMap[groupName];
                                  break;
                                }
                              }
                            }
                            return relationOptions ? (
                              relationOptions.map((option) => (
                                <MenuItem key={option.id || option.value} value={option.text || option.optionText}>
                                  {option.text || option.optionText}
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem disabled>No options available</MenuItem>
                            );
                          })()}
                        </Select>
                      </Grid>
                    </Grid>

                    <Grid container size={12}>
                      <Grid item size={4}>
                        <Select
                          fullWidth
                          displayEmpty
                          size="small"
                          value={formData.nomineeOccupation || ""}
                          onChange={(e) =>
                            handleInputChange("nomineeOccupation", e.target.value)
                          }
                          disabled={!isMemberSaved}
                          sx={textFieldStyleAccent}
                          renderValue={(value) => {
                            if (!value) return "Occupation";
                            return value;
                          }}
                        >
                          <MenuItem value="" disabled>
                            Select Occupation
                          </MenuItem>
                          {(() => {
                            let occupationOptions = optionsMap.OCCUPATION || optionsMap['Occupation'];
                            if (!occupationOptions) {
                              const possibleGroups = ['OCCUPATION', 'OCCUPATION_TYPE', 'MEMBER_OCCUPATION'];
                              for (const groupName of possibleGroups) {
                                if (optionsMap[groupName]) {
                                  occupationOptions = optionsMap[groupName];
                                  break;
                                }
                              }
                            }
                            return occupationOptions ? (
                              occupationOptions.map((option) => (
                                <MenuItem key={option.id} value={option.text}>
                                  {option.text}
                                </MenuItem>
                              ))
                            ) : null;
                          })()}
                        </Select>
                      </Grid>
                      <Grid item size={4}>
                        <TextField
                          fullWidth
                          label="Mobile"
                          size="small"
                          value={formData.nomineeMobileNo}
                          onChange={(e) =>
                            handleInputChange("nomineeMobileNo", e.target.value)
                          }

                          disabled={!isMemberSaved}
                          sx={textFieldStyleAccent}
                        />
                      </Grid>
                      <Grid item size={4}>
                        <DateInput
                          fullWidth
                          label="DOB"
                          size="small"
                          value={formData.nomineeDob}
                          onChange={(value) =>
                            handleInputChange("nomineeDob", value)
                          }
                          disabled={!isMemberSaved}
                          sx={textFieldStyleAccent}
                        />
                      </Grid>
                    </Grid>
                    <Grid item size={12}>
                      <TextField
                        fullWidth
                        label="Nominee Address"
                        size="small"
                        value={formData.nomineeAddress}
                        onChange={handleNomineeAddressChange}
                        disabled={formData.nomineeAddressSame || !isMemberSaved}
                        // multiline
                        rows={2}
                        maxRows={4}
                        sx={textFieldStyleAccent}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={formData.nomineeAddressSame}
                            onChange={handleCheckboxChange}
                            disabled={!isMemberSaved}
                          />
                        }
                        label="Same as Member"
                      />
                    </Grid>

                    {/* Nominee KYC Details */}
                    <Grid container size={12} sx={{ mb: 2 }}>
                      <Grid item size={6}>
                        <Box sx={{ width: "100%" }}>
                          <TextField
                            fullWidth
                            label="Nominee Aadhaar No."
                            size="small"
                            value={formData.nomineeAadhaarNo || ""}
                            onChange={(e) =>
                              handleInputChange("nomineeAadhaarNo", e.target.value)
                            }
                            disabled={!isMemberSaved}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment
                                  position="end"
                                  sx={{ margin: 0 }}
                                >
                                  <Button
                                    onClick={() =>
                                      handleOpenFileUpload(
                                        "nomineeAadharFiles",
                                        "Nominee Aadhaar"
                                      )
                                    }
                                    variant="contained"
                                    size="small"
                                    disabled={!isMemberSaved}
                                    sx={{
                                      fontSize: "0.7rem",
                                      textTransform: "none",
                                      minWidth: "auto",
                                      color: "white",
                                      px: 1,
                                      height: "100%",
                                      backgroundColor:
                                        formData.nomineeAadharFiles &&
                                          formData.nomineeAadharFiles.length > 0
                                          ? colors.success[500]
                                          : colors.error[500],
                                      borderTopLeftRadius: 0,
                                      borderBottomLeftRadius: 0,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <CloudUpload
                                      sx={{ fontSize: "1.5rem" }}
                                    />
                                  </Button>
                                </InputAdornment>
                              ),
                              sx: {
                                paddingRight: 0,
                              },
                            }}
                            sx={{
                              ...textFieldStyleAccent,
                              "& .MuiOutlinedInput-root": {
                                paddingRight: 0,
                              },
                            }}
                          />
                        </Box>
                      </Grid>

                      <Grid item size={6}>
                        <Box sx={{ width: "100%" }}>
                          <TextField
                            fullWidth
                            label="Nominee PAN No."
                            size="small"
                            value={formData.nomineePanNo || ""}
                            onChange={(e) =>
                              handleInputChange("nomineePanNo", e.target.value)
                            }
                            disabled={!isMemberSaved}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment
                                  position="end"
                                  sx={{ margin: 0 }}
                                >
                                  <Button
                                    onClick={() =>
                                      handleOpenFileUpload(
                                        "nomineePanFiles",
                                        "Nominee PAN"
                                      )
                                    }
                                    variant="contained"
                                    size="small"
                                    disabled={!isMemberSaved}
                                    sx={{
                                      fontSize: "0.7rem",
                                      textTransform: "none",
                                      minWidth: "auto",
                                      color: "white",
                                      px: 1,
                                      height: "100%",
                                      backgroundColor:
                                        formData.nomineePanFiles &&
                                          formData.nomineePanFiles.length > 0
                                          ? colors.success[500]
                                          : colors.error[500],
                                      borderTopLeftRadius: 0,
                                      borderBottomLeftRadius: 0,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <CloudUpload
                                      sx={{ fontSize: "1.5rem" }}
                                    />
                                  </Button>
                                </InputAdornment>
                              ),
                              sx: {
                                paddingRight: 0,
                              },
                            }}
                            sx={{
                              ...textFieldStyleAccent,
                              "& .MuiOutlinedInput-root": {
                                paddingRight: 0,
                              },
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Bottom Action Area */}
                  <Grid container spacing={1}>
                    <Grid item size={12}>
                      <Grid item size={12} >
                        <Select
                          fullWidth
                          value={formData.membershipStatus || ""}
                          onChange={(e) => handleInputChange("membershipStatus", e.target.value)}
                          displayEmpty
                          size="small"
                          sx={{
                            minWidth: 200,
                            mt: 1,
                            '& .MuiSelect-select': {
                              py: 1,
                            }
                          }}
                        >
                          <MenuItem value="" disabled>
                            Membership Status
                          </MenuItem>
                          {membershipStatusOptions.map((option) => (
                            <MenuItem key={option.id} value={option.text}>
                              {option.text}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                      <Grid container size={12} spacing={2} sx={{ display: 'flex', flexDirection: 'row' }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "wrap",
                            gap: 0.5,
                          }}
                          mt={2}
                        >
                          <FormControlLabel
                            sx={{
                              color: "red",
                              fontWeight: "bold",
                              fontSize: "0.75rem",
                              "& .MuiFormControlLabel-label": {
                                fontSize: "0.75rem",
                              },
                            }}
                            control={<Checkbox size="small" />}
                            label="All Operations Blocked"
                          />
                          <FormControlLabel
                            sx={{
                              fontSize: "0.75rem",
                              "& .MuiFormControlLabel-label": {
                                fontSize: "0.75rem",
                              },
                            }}
                            control={<Checkbox size="small" />}
                            label="E-Statement"
                          />
                          <FormControlLabel
                            sx={{
                              fontSize: "0.75rem",
                              "& .MuiFormControlLabel-label": {
                                fontSize: "0.75rem",
                              },
                            }}
                            control={<Checkbox size="small" />}
                            label="E-Receipt"
                          />
                          <FormControlLabel
                            sx={{
                              fontSize: "0.75rem",
                              "& .MuiFormControlLabel-label": {
                                fontSize: "0.75rem",
                              },
                            }}
                            control={<Checkbox size="small" />}
                            label="E-Receipt and Print"
                          />
                          <FormControlLabel
                            sx={{
                              fontSize: "0.75rem",
                              "& .MuiFormControlLabel-label": {
                                fontSize: "0.75rem",
                              },
                            }}
                            control={<Checkbox size="small" />}
                            label="RE-KYC"
                          />
                        </Box>
                      </Grid>
                      {/* Conditional date fields for "Left" status */}
                      {formData.membershipStatus === "Left" && (
                        <Grid container size={12} spacing={2} sx={{ display: 'flex', flexDirection: 'row', mt: 2 }}>
                          <Grid item size={6}>
                          <DateInput
                            fullWidth 
                            label="Meeting Date"
                            size="small"
                            value={formData.meetingDate || ""}
                            onChange={(value) => handleInputChange("meetingDate", value)}
                            disabled={!isMemberSaved}
                            sx={{
                              minWidth: 200,
                              ...textFieldStylePrimary
                            }}
                          />
                          </Grid>

                          <Grid item size={6}>
                          <DateInput
                            label="Resignation Date"
                             fullWidth 
                            size="small"
                            value={formData.resignationDate || ""}
                            onChange={(value) => handleInputChange("resignationDate", value)}
                            disabled={!isMemberSaved}
                            sx={{
                              minWidth: 200,
                              ...textFieldStylePrimary
                            }}
                          />
                          </Grid>
                        </Grid>
                      )}

                        <Grid item size={12} >
                        <Button
                          variant="contained"
                          startIcon={<Email />}
                          fullWidth
                          sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1.5,
                            mt: 2,
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.9rem",
                            backgroundColor: "#ff6b6b",
                            color: "#fff",
                            boxShadow: "0 4px 15px rgba(255, 107, 107, 0.3)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              backgroundColor: "#ff5252",
                              transform: "translateY(-2px)",
                              boxShadow: "0 6px 20px rgba(255, 107, 107, 0.4)",
                            },
                            "&:active": {
                              transform: "translateY(0px)",
                            },
                          }}
                        >
                          Send Email
                        </Button>
                        </Grid>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* File Upload Popup */}
      <FileUploadPopup
        key={fileUploadPopup.field} // Force re-render when field changes
        open={fileUploadPopup.open}
        onClose={handleCloseFileUpload}
        onFilesSelected={handleFilesSelected}
        title={fileUploadPopup.title}
        existingFiles={fileUploadPopup.existingFiles || []}
        maxFiles={6}
        accept="image/*"
        multiple={true}
      />
    </Box>
  );
};

export default MemberForm;
